import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import QtyInput from './QtyInput';
import { useIsMobile } from '../useIsMobile';
import BusinessInfoModal from './BusinessInfoModal';
import { classifyItem, CATEGORY_META, CATEGORY_ORDER, CATEGORY_KEY_TO_LABEL } from '../utils/itemCategory';
import BiName, { buildZhMap, zhOf } from '../i18n/BiName';   // V100 한중 병기
import { useLang } from '../i18n/LangContext';   // V105 UI 다국어
import { safeLinkHref, linkLabel } from '../utils/purchaseLink';   // V118 구매 링크
import { searchVariants } from '../utils/hangulKeys';   // V122 영타로 친 한글도 검색
import { pronText, usePinyinReady, isPinyinReady, ensurePinyin } from '../i18n/pron';   // V127 인쇄·엑셀 발음
import { specToZh, unitToZh } from '../i18n/units';   // V131 규격·단위 중국어
import { tr } from '../i18n/ui';   // V133 중국어 발주서
import { orderPackInfo, packQty, lineAmount, unitPriceOf, needInUnits, fmtQty } from '../utils/orderPack';   // V135 수량 1 = 규격만큼
import { doc, setDoc } from 'firebase/firestore';   // V136 매장 국가 바로 바꾸기
import { db } from '../db';
import { orderKeyOf } from '../utils/orderKey';   // V142 발주 수량은 단가표 줄마다 (같은 이름 다른 발주처 구별)
import { mergeOrderEntries, canMergeOrders, savedTimeOf, entryVendor, applyQtyEdits, splitByVendor } from '../utils/orderMerge';   // V137 합치기 · V138 발주처별 저장·수량 고치기

const getToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

function PurchaseOrder({ prices, onPricesChange, orderQty, onOrderQtyChange, categories = [], orderNeed = {}, onOrderNeedChange,
                         orderHistory = [], onSaveOrderHistory, onDeleteOrderHistory,
                         ownerUid, canChangeCountry = false,   // V136
                         onMergeOrderHistory, onUpdateOrderHistory }) {   // V137 · V138

  // V142: 같은 이름(계란)이 발주처별로 여러 줄일 수 있어서 이름이 아니라 그 줄만 바꾼다
  const sameRow = (p, item) => orderKeyOf(p) === orderKeyOf(item);
  // V91: 안전재고 수정 — 단가표(prices)에 safetyStock 필드로 저장
  const updateSafetyStock = (item, value) => {
    if (typeof onPricesChange !== 'function') return;
    const v = Math.max(0, Number(value) || 0);
    const next = (prices || []).map(p =>
      sameRow(p, item) ? { ...p, safetyStock: v } : p
    );
    onPricesChange(next);
  };

  // V92: 발주서 품목 감추기 (prices[].hiddenInOrder 필드 토글)
  const toggleHiddenInOrder = (item) => {
    if (typeof onPricesChange !== 'function') return;
    const next = (prices || []).map(p =>
      sameRow(p, item) ? { ...p, hiddenInOrder: !p.hiddenInOrder } : p
    );
    onPricesChange(next);
  };

  const [search, setSearch] = useState('');
  const [showHidden, setShowHidden] = useState(false);  // V92: 숨김 품목 보기 토글

  // ── V100 한중 병기 ──────────────────────────────────────────────────
  // 중국어는 단가표(prices[].name_zh)가 유일한 출처다.
  // 발주 내역(orderHistory)에 저장된 과거 항목은 name_zh가 없을 수 있으므로
  // 항상 현재 단가표에서 이름으로 되찾는다.
  const { t, fmt: money, lang, country } = useLang();   // V105 문구 / V108 매장 통화
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);   // V147 화면 문구 (중국어 화면이면 중국어만)
  const vName = (v) => (v === '미분류' ? Z('미분류', '未分类') : vendorLabel(v));   // V147 발주처 표시
  const zhMap = useMemo(() => buildZhMap(prices), [prices]);
  // V118: 발주처 이름 한중 병기 — 단가표의 vendor_zh (한글 섞인 옛 번역은 안 씀)
  const vendorZhMap = useMemo(() => {
    const m = new Map();
    for (const p of prices || []) {
      const v = String(p?.vendor || '').trim();
      const z = String(p?.vendor_zh || '').trim();
      if (v && z && !/[가-힣]/.test(z) && !m.has(v)) m.set(v, z);
    }
    return m;
  }, [prices]);
  const vendorLabel = (v) => { const z = vendorZhMap.get(String(v || '').trim()); return z ? `${v} · ${z}` : v; };
  // V127: 인쇄·엑셀에도 발음 — 한국어 화면은 중국어 밑에 병음, 중국어 화면은 한국어 밑에 로마자
  const pyReady = usePinyinReady(lang !== 'zh');   // 인쇄·엑셀 누르기 전에 병음 사전을 받아 둔다
  const pronOf = (name) => pronText(name, zhOf(zhMap, name), lang);
  // 인쇄 HTML용 — 한국어 아래 줄에 중국어 (+ 발음)
  const zhLine = (name) => {
    const z = zhOf(zhMap, name);
    const pr = pronOf(name);
    const prDiv = pr ? `<div style="font-size:10px;color:#9ca3af;font-weight:400;margin-top:1px">${String(pr).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))}</div>` : '';
    return (lang === 'zh' ? prDiv : '')
      + (z ? `<div style="font-size:11px;color:#6b7280;font-weight:400;margin-top:2px">${z}</div>` : '')
      + (lang === 'zh' ? '' : prDiv);
  };
  // 엑셀용 — 한 칸에 "간장 / 酱油 (jiàng yóu)" · 중국어 화면은 "간장 (gan jang) / 酱油"
  const xlsName = (name) => {
    const z = zhOf(zhMap, name);
    const pr = pronOf(name);
    return lang === 'zh'
      ? `${name}${pr ? ` (${pr})` : ''}${z ? ` / ${z}` : ''}`
      : `${name}${z ? ` / ${z}` : ''}${pr ? ` (${pr})` : ''}`;
  };
  // 텍스트 공유용 — 같은 줄에 " / 中文"
  const zhInline = (name) => {
    const z = zhOf(zhMap, name);
    return z ? ` / ${z}` : '';
  };

  // ── V119: 발주서 출력물을 발주처별로 묶는다 ───────────────────────────────
  // 예전엔 화면에서만 발주처별로 보이고, 저장·전송한 발주서(PDF·이메일·엑셀)에는 발주처가 아예 없었다.
  // 발주처가 하나뿐이어도 이름은 꼭 찍는다. 중국어 화면·중국 매장이면 표 머리글도 한중 병기.
  const biPrint = lang === 'zh' || country === 'CN';
  // V133: 중국 매장·중국어 화면의 발주서는 업체(중국)에 보내는 문서 → 중국어로만 만든다.
  //       머리글·제목·발주처·품목·규격·단위 전부 중국어 (예전엔 "재료명 / 食材名称"처럼 병기).
  //       품목은 중국어 아래 작게 한국어를 남겨 사장님이 맞춰 볼 수 있게 한다. 발음 줄·금액은 넣지 않는다.
  const cnDoc = biPrint;
  const L = (ko, zh) => (cnDoc ? zh : ko);
  // ── V135: 중국 매장은 발주 수량 1 = 규격만큼 (감자 규격 10근·단위 근 → 1 = 10근, 2 = 20근)
  //    발주서·내역에는 실제 양(10근)을 찍는다. 단가가 한 묶음 값인지 1근 값인지는 기준량(g)으로 판단 (utils/orderPack.js)
  //    한국 매장은 그대로 (수량 1 = 단위 1개)
  const packMode = country === 'CN';
  const packOf = (item) => orderPackInfo(item, packMode);
  const keyOf = (item) => orderKeyOf(item);   // V142 발주 수량·필요량의 키
  const qtyOf = (item) => orderQty[keyOf(item)] || 0;
  const docQtyOf = (item) => packQty(qtyOf(item), packOf(item));   // 실제 발주량 (단위 기준)
  const amountOf = (item) => lineAmount(qtyOf(item), item.price || 0, packOf(item));
  const NO_VENDOR = '미분류';
  const vendorDocName = (v) => {
    const z = vendorZhMap.get(String(v || '').trim());
    return cnDoc ? (z || v) : (z ? `${v} · ${z}` : v);
  };
  const vendorTitle = (v) => (!v || v === NO_VENDOR) ? L('발주처 미지정', '未指定供应商') : vendorDocName(v);
  // 사업자 정보 줄 (중국어 발주서는 라벨도 중국어)
  const bizParts = (bi) => {
    const parts = [];
    if (bi && bi.regNum) parts.push(L('사업자번호 ', '税号 ') + bi.regNum);
    if (bi && bi.ceo) parts.push(L('대표 ', '负责人 ') + bi.ceo);
    if (bi && bi.phone) parts.push('☎ ' + bi.phone);
    if (bi && bi.address) parts.push(bi.address);
    return parts;
  };
  const docTitleOf = (bn) => (cnDoc ? (bn ? `${bn} 订货单` : '订货单') : (bn ? `${bn} 발주서` : '발주서'));
  const groupItemsByVendor = (items) => {
    const m = new Map();
    for (const it of items || []) {
      const v = String(it?.vendor || '').trim() || NO_VENDOR;
      if (!m.has(v)) m.set(v, []);
      m.get(v).push(it);
    }
    return [...m.keys()]
      .sort((a, b) => (a === NO_VENDOR ? 1 : b === NO_VENDOR ? -1 : a.localeCompare(b, 'ko')))
      .map(v => ({ vendor: v, items: m.get(v) }));
  };
  const escHtml = (x) => String(x == null ? '' : x).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const linkOfName = (name) => safeLinkHref((prices || []).find(x => x.name === name)?.link);
  const linkHtml = (name) => {
    const href = linkOfName(name);
    return href ? `<div style="margin-top:2px"><a href="${escHtml(href)}" style="font-size:11px;color:#1A56A0;text-decoration:none">🔗 ${escHtml(linkLabel(href))}</a></div>` : '';
  };
  // ── V131: 규격·단위 중국어 ───────────────────────────────────────────────
  // 예전엔 중국어 화면·중국 매장이어도 발주서의 규격("10근", "2근/봉")·단위("근", "봉")가 한국어로만 찍혔다.
  // 고정 표(units.js: 근→斤, 봉→袋 …)로 먼저 바꾸고, 못 바꾸면 단가표의 spec_zh / unit_zh.
  // 중국어 화면이거나 중국 매장일 때만 병기한다(biPrint) — 한국 매장 한국어 화면은 그대로.
  const priceByName = useMemo(() => {
    const m = new Map();
    for (const p of prices || []) if (p && p.name && !m.has(p.name)) m.set(p.name, p);
    return m;
  }, [prices]);
  const specZh = (name, spec) => {
    const sp = String(spec == null ? '' : spec).trim();
    if (!sp || sp === '-') return '';
    const p = priceByName.get(name);
    const z = specToZh(sp) || (p && String(p.spec || '').trim() === sp ? String(p.spec_zh || '').trim() : '');
    return z && z !== sp && !/[가-힣]/.test(z) ? z : '';
  };
  const unitZh = (name, unit) => {
    const u = String(unit == null ? '' : unit).trim() || '개';
    const p = priceByName.get(name);
    const z = unitToZh(u) || (p && String(p.unit || '').trim() === u ? String(p.unit_zh || '').trim() : '');
    return z && z !== u && !/[가-힣]/.test(z) ? z : '';
  };
  // 글자용 (엑셀·텍스트): "10근 / 10斤", "근 / 斤"
  const specText = (name, spec) => { const sp = spec || '-'; const z = biPrint ? specZh(name, sp) : ''; return z ? `${sp} / ${z}` : sp; };
  const unitText = (name, unit) => { const u = unit || '개'; const z = biPrint ? unitZh(name, u) : ''; return z ? `${u} / ${z}` : u; };
  // V135: 단가 표시 — 한 묶음 값이면 '¥15/10근', 1단위 값이면 '¥1.5/근'
  const priceLabel = (item) => `${money(item.price || 0)}/${packOf(item).perPack ? specText(item.name, item.spec) : unitText(item.name, item.unit)}`;
  const unitShow = (item) => { const u = item.unit || '개'; return lang === 'zh' ? (unitZh(item.name, u) || u) : u; };
  // 레시피·재고에서 넘어온 필요량 (단위 기준)
  const needText = (item) => `${needInUnits(orderNeed[keyOf(item)], packOf(item)).toFixed(2)} ${unitText(item.name, item.unit)}`;
  // 인쇄 HTML용: 한국어 아래 줄에 중국어
  const zhSubHtml = (z) => (z ? `<div style="font-size:11px;color:#6b7280;font-weight:400;margin-top:2px">${escHtml(z)}</div>` : '');
  // V133: 문서(PDF·이메일 표·엑셀·텍스트)용 — 중국어 발주서는 중국어만(못 바꾸면 원문), 한국 발주서는 한국어만
  const docSpec = (name, spec) => { const sp = spec || '-'; return cnDoc ? (specZh(name, sp) || sp) : sp; };
  const docUnit = (name, unit) => { const u = unit || '개'; return cnDoc ? (unitZh(name, u) || u) : u; };
  const specHtml = (name, spec) => escHtml(docSpec(name, spec));
  const qtyHtml = (name, qty, unit) => `${fmtQty(qty)} ${escHtml(docUnit(name, unit))}`;
  // 품목 이름: 중국어 발주서는 중국어(없으면 한국어) + 아래 작게 한국어 / 한국 발주서는 기존대로(한국어 + 중국어·발음 줄)
  const docNameHtml = (name) => {
    if (!cnDoc) return `${name}${zhLine(name)}`;
    const z = zhOf(zhMap, name);
    return z ? `${escHtml(z)}<div style="font-size:10px;color:#9ca3af;font-weight:400;margin-top:2px">${escHtml(name)}</div>` : escHtml(name);
  };
  const docNameText = (name) => (cnDoc ? (zhOf(zhMap, name) || name) : `${name}${zhInline(name)}`);
  const docNameXls = (name) => {
    if (!cnDoc) return xlsName(name);
    const z = zhOf(zhMap, name);
    return z ? `${z} / ${name}` : name;
  };

  // 인쇄용(PDF) 발주처 묶음 — rows: [{ name, spec, unit, qty, vendor }]
  const PRINT_GROUP_CSS = '.vgroup{margin-bottom:18px;page-break-inside:avoid}'
    + '.vhead{background:#1e40af;color:#fff;padding:8px 12px;font-size:13px;font-weight:800;border-radius:6px 6px 0 0}'
    + '.vcount{font-weight:400;font-size:11px;opacity:.85;margin-left:6px}';
  const printVendorSections = (rows) => groupItemsByVendor(rows).map(g => {
    const body = g.items.map((it, i) => `<tr>
        <td style="text-align:center;color:#9ca3af;font-size:12px">${i + 1}</td>
        <td style="font-weight:600">${docNameHtml(it.name)}${linkHtml(it.name)}</td>
        <td style="color:#6b7280">${specHtml(it.name, it.spec)}</td>
        <td style="text-align:right;font-weight:700;color:#1e40af">${qtyHtml(it.name, it.qty, it.unit)}</td>
      </tr>`).join('');
    return `<div class="vgroup">
      <div class="vhead">🏪 ${escHtml(vendorTitle(g.vendor))}<span class="vcount">${L(`${g.items.length}품목`, `${g.items.length}项`)}</span></div>
      <table><thead><tr>
        <th style="width:36px">NO</th><th style="text-align:left">${L('재료명', '食材名称')}</th><th style="text-align:left">${L('규격', '规格')}</th><th style="text-align:right">${L('발주 수량', '订货数量')}</th>
      </tr></thead><tbody>${body}</tbody></table>
    </div>`;
  }).join('');
  const rightPanelRef = useRef(null);
  const rowRefs = useRef({});

  const isMobile = useIsMobile();

  // ── V134: 왼쪽 원재료 목록 폭 — 가운데 막대를 끌어서 넓히고 좁힌다 (두 번 누르면 기본 폭). 기기에 기억
  const LEFT_W_KEY = 'semfit_po_left_w';
  const leftPanelRef = useRef(null);
  const [leftW, setLeftW] = useState(() => {
    try { const v = Number(localStorage.getItem(LEFT_W_KEY)); return v >= 280 ? v : null; } catch (e) { return null; }
  });
  const [resizing, setResizing] = useState(false);
  const clampLeftW = (w) => {
    const max = Math.max(320, (typeof window !== 'undefined' ? window.innerWidth : 1400) - 380);   // 오른쪽 발주서 칸은 최소 380px 남긴다
    return Math.round(Math.min(max, Math.max(280, w)));
  };
  const onResizeDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* 오래된 브라우저 */ }
    setResizing(true);
  };
  const onResizeMove = (e) => {
    if (!resizing) return;
    const left = leftPanelRef.current ? leftPanelRef.current.getBoundingClientRect().left : 0;
    setLeftW(clampLeftW(e.clientX - left));
  };
  const onResizeUp = () => {
    if (!resizing) return;
    setResizing(false);
    try { if (leftW) localStorage.setItem(LEFT_W_KEY, String(leftW)); } catch (_) { /* 저장 못 해도 이번엔 그대로 */ }
  };
  const resetLeftW = () => {
    setLeftW(null);
    try { localStorage.removeItem(LEFT_W_KEY); } catch (_) { /* 무시 */ }
  };
  const [mobilePanelTab, setMobilePanelTab] = useState('list'); // 'list' | 'order'

  // ── 사업자 이름 (localStorage)
  const [bizName, setBizName] = useState(() => localStorage.getItem('ddabak_biz_name') || '');
  const [editingBizName, setEditingBizName] = useState(false);
  const [bizNameDraft, setBizNameDraft] = useState('');

  const saveBizName = () => {
    const name = bizNameDraft.trim();
    setBizName(name);
    localStorage.setItem('ddabak_biz_name', name);
    setEditingBizName(false);
  };

  const [bizInfo, setBizInfo] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ddabak_biz_info') || '{}'); }
    catch { return {}; }
  });
  const [showBizModal, setShowBizModal] = useState(false);
  const handleSaveBizInfo = (next) => {
    setBizInfo(next);
    localStorage.setItem('ddabak_biz_info', JSON.stringify(next));
    if (next.name && next.name !== bizName) {
      setBizName(next.name);
      localStorage.setItem('ddabak_biz_name', next.name);
    }
  };

  const bizInfoText = useMemo(() => {
    const parts = bizParts(bizInfo);   // V133
    return parts.join(' · ');
  }, [bizInfo, cnDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── V136: 매장 국가가 '한국'인데 중국 매장으로 보이면 발주서 화면에서 바로 바꾸게 한다
  //    설정 탭이 상단 탭 맨 끝이라 화면 밖으로 밀려 안 보였고, 그래서 JJGG가 계속 '한국'으로 남아
  //    ¥ 표시·중국어 발주서·수량 1 = 규격만큼(V135)이 꺼져 있었다.
  //    중국 매장으로 보는 기준: 중국어 화면이거나, 발주처 이름이 한자만인 품목이 20% 이상. 사장님(owner)만, '나중에'로 숨김
  const CN_BANNER_KEY = `semfit_cn_banner_off_${ownerUid || ''}`;
  const [cnBannerOff, setCnBannerOff] = useState(() => { try { return localStorage.getItem(CN_BANNER_KEY) === '1'; } catch (e) { return false; } });
  const [switchingCountry, setSwitchingCountry] = useState(false);
  const looksChina = useMemo(() => {
    if (lang === 'zh') return true;
    const vs = (prices || []).map(p => String(p.vendor || '').trim()).filter(Boolean);
    if (!vs.length) return false;
    return vs.filter(v => /[\u4e00-\u9fff]/.test(v) && !/[가-힣]/.test(v)).length / vs.length >= 0.2;
  }, [prices, lang]);
  const showCnBanner = country !== 'CN' && canChangeCountry && !!ownerUid && looksChina && !cnBannerOff;
  const hideCnBanner = () => { try { localStorage.setItem(CN_BANNER_KEY, '1'); } catch (e) {} setCnBannerOff(true); };
  const switchToChina = async () => {
    const nm = bizName ? `‘${bizName}’ ` : '';
    const msg = lang === 'zh'
      ? `将${nm}门店所在国改为中国。\n\n金额显示为¥，订货数量1 = 一个规格（例：土豆10斤），订货单为中文。\n已输入的数字不会改变。\n（可随时在 设置 → 🌏 门店所在国 改回）\n\n继续吗？`
      : `${nm}매장의 국가를 中国(중국)으로 바꿉니다.\n\n금액이 ¥로 표시되고, 발주 수량 1 = 규격만큼(예: 감자 10근), 발주서는 중국어로 만들어집니다.\n이미 입력된 숫자는 바뀌지 않습니다.\n(설정 탭 → 🌏 매장 국가에서 언제든 다시 바꿀 수 있어요)\n\n계속할까요?`;
    if (!window.confirm(msg)) return;
    setSwitchingCountry(true);
    try { await setDoc(doc(db, 'users', ownerUid), { country: 'CN' }, { merge: true }); }
    catch (e) { alert((lang === 'zh' ? '保存失败: ' : '저장 실패: ') + (e?.message || e)); }
    finally { setSwitchingCountry(false); }
  };

  // ── 규격 표시 토글 (품목별, localStorage 영구화) ──
  const [specExclude, setSpecExclude] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ddabak_spec_exclude') || '{}'); }
    catch { return {}; }
  });
  // V142: 줄 키로 기억 (예전 기록은 품목 이름으로 남아 있어서 이름도 같이 본다)
  const isSpecExcluded = (item) => !!(item && (specExclude[orderKeyOf(item)] || specExclude[item.name]));
  const toggleSpecExclude = (item) => {
    setSpecExclude(prev => {
      const next = { ...prev };
      const was = !!(next[orderKeyOf(item)] || next[item.name]);
      delete next[item.name];
      if (was) delete next[orderKeyOf(item)];
      else next[orderKeyOf(item)] = true;
      localStorage.setItem('ddabak_spec_exclude', JSON.stringify(next));
      return next;
    });
  };
  const getEffSpec = (item) => isSpecExcluded(item) ? '' : (item?.spec || '-');

  const bizInfoHtml = useMemo(() => {
    const parts = bizParts(bizInfo);   // V133
    if (parts.length === 0) return '';
    return '<div style="font-size:11px;color:#6b7280;margin-top:4px;line-height:1.5">' + parts.map(escHtml).join(' · ') + '</div>';
  }, [bizInfo, cnDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 발주 내역 모달
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyDetail, setHistoryDetail] = useState(null); // 상세 보기 중인 항목
  const [hVendorFilter, setHVendorFilter] = useState('all'); // 내역 상세 업체 필터
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo,   setHistoryDateTo]   = useState('');
  const [historyListVendor, setHistoryListVendor] = useState('all');
  const [saveMemo, setSaveMemo] = useState('');
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [savedNote, setSavedNote] = useState(null);   // V137 방금 저장한 발주서 { count, total, nth }
  const [mergeSel, setMergeSel] = useState([]);       // V137 합칠 발주서 id들
  const [entryCopied, setEntryCopied] = useState(''); // V137 내역 글 복사 상태
  const [hEdit, setHEdit] = useState(null);           // V138 내역 수량 고치기 { 품목 순번: 값 } | null

  const handleSaveOrder = () => {
    if (orderedItems.length === 0) { alert(Z('발주서가 비어있어요.', '订货单是空的。')); return; }
    setShowSaveConfirm(true);
    setSaveMemo('');
  };

  const confirmSave = () => {
    const today = getToday();
    const toSaved = (item) => {
      const pk = packOf(item);   // V135: 묶음이면 실제 양(10근)과 1근 값으로 저장 — 내역·재고 계산은 예전 그대로 동작
      return {
        name: item.name,
        spec: item.spec || '-',
        unit: item.unit || '개',
        qty: docQtyOf(item),
        price: unitPriceOf(item.price || 0, pk),
        vendor: item.vendor?.trim() || '미분류',
        ...(pk.pack > 1 ? { packs: qtyOf(item), pack: pk.pack } : {}),
      };
    };
    // V138: 발주처별로 따로 저장 — 시장 발주서, 타오바오 발주서 …
    const entries = splitByVendor(
      { date: today, bizName: bizName || '', bizInfo: bizInfo || {}, memo: saveMemo.trim() },
      groupItemsByVendor(orderedItems).map(g => ({
        vendor: g.vendor,
        items: g.items.map(toSaved),
        totalCost: g.items.reduce((s, it) => s + amountOf(it), 0),
      })),
    );
    onSaveOrderHistory(entries);
    setShowSaveConfirm(false);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
    // V137: 저장한 발주서는 비운다 — 추가 발주(양파 저장 → 감자 발주)에는 새로 담은 것만 들어가서
    //       발주서가 따로따로 저장된다. 예전엔 앞서 저장한 품목이 다음 저장에 또 들어가 금액이 두 번 잡혔다
    const cleared = {};
    orderedItems.forEach(it => { cleared[keyOf(it)] = 0; });
    onOrderQtyChange(prev => ({ ...prev, ...cleared }));
    if (onOrderNeedChange) onOrderNeedChange(prev => ({ ...prev, ...cleared }));
    setSavedNote({ date: today, total: totalCost,
      list: entries.map(e => ({ vendor: e.vendor, count: e.items.length, total: e.totalCost })) });
  };

  // ── 공유 모달
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTab, setShareTab] = useState('save'); // 'save' | 'send'
  // ── 미리보기 모달 { mode: 'email'|'sms', contact }
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(false);

  // ── 연락처 (localStorage)
  const [contacts, setContacts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ddabak_contacts') || '[]'); }
    catch { return []; }
  });
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [cf, setCf] = useState({ name: '', email: '', phone: '' });

  // Kakao SDK 초기화
  useEffect(() => {
    if (window.Kakao && !window.Kakao.isInitialized()) {
      const key = process.env.REACT_APP_KAKAO_KEY;
      if (key) window.Kakao.init(key);
    }
  }, []);

  // ── 반제품·판매메뉴 이름 목록
  const semiNames = useMemo(() => new Set(
    categories.flatMap(c => c.items.filter(i => i.type === 'semi').map(i => i.name))
  ), [categories]);
  const menuNames = useMemo(() => new Set(
    categories.flatMap(c => c.items.filter(i => i.type === 'menu').map(i => i.name))
  ), [categories]);

  // V80: 발주 대상 = 신선식품/공산품/소모품 (반제품·판매메뉴 제외)
  // legacy '원재료'도 포함. classifyItem이 그룹 헤더 분류해줌.
  const rawMaterials = useMemo(() =>
    prices.map((p, i) => ({ ...p, _idx: i }))
      .filter(p => {
        const cat = p.category || '';
        if (cat === '반제품') return false;
        if (semiNames.has(p.name)) return false;
        if (menuNames.has(p.name)) return false;
        return true;
      }),
    [prices, semiNames, menuNames]
  );

  // V92: 숨김 품목 카운트
  const hiddenCount = useMemo(
    () => rawMaterials.filter(p => p.hiddenInOrder).length,
    [rawMaterials]
  );

  // V93: 1차 vendor, 2차 category(신선→공산→소모→반제품), 3차 name
  const CAT_SORT_ORDER = { '신선식품': 1, '공산품': 2, '소모품': 3, '반제품': 4 };
  const resolveCat = (p) => p.category || CATEGORY_KEY_TO_LABEL[classifyItem(p)] || '미분류';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = showHidden
      ? rawMaterials
      : rawMaterials.filter(p => !p.hiddenInOrder);
    // V122: 영타로 친 한글(rkswkd → 간장)과 중국어 이름으로도 찾는다
    const qs = q ? [...new Set(searchVariants(search.trim()).map(v => v.toLowerCase()))] : [];
    const searched = q
      ? base.filter(p => qs.some(v => p.name?.toLowerCase().includes(v) || p.spec?.toLowerCase().includes(v) || p.name_zh?.includes(v)))
      : base;
    // V93: vendor → category → name 정렬
    const sorted = [...searched].sort((a, b) => {
      const va = (a.vendor || '').trim();
      const vb = (b.vendor || '').trim();
      if (va && !vb) return -1;
      if (!va && vb) return 1;
      if (va !== vb) return va.localeCompare(vb, 'ko');
      const ca = CAT_SORT_ORDER[resolveCat(a)] || 9;
      const cb = CAT_SORT_ORDER[resolveCat(b)] || 9;
      if (ca !== cb) return ca - cb;
      return (a.name || '').localeCompare(b.name || '', 'ko');
    });
    return sorted;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawMaterials, search, showHidden]);

  // V142: key = 단가표 줄 키 (orderKeyOf)
  const setQty = useCallback((key, val) => {
    const next = Math.max(0, val);
    onOrderQtyChange(prev => ({ ...prev, [key]: next }));
    if (next > 0) setTimeout(() => scrollToRow(key), 80);
  }, [onOrderQtyChange]);

  const scrollToRow = (key) => {
    const el = rowRefs.current[key];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.transition = 'background 0.1s';
      el.style.background = '#fef9c3';
      setTimeout(() => { if (el) el.style.background = ''; }, 1200);
    }
  };

  useEffect(() => {
    if (!search.trim()) return;
    const first = filtered.find(p => (orderQty[orderKeyOf(p)] || 0) > 0);
    if (first) {
      const timer = setTimeout(() => scrollToRow(orderKeyOf(first)), 150);
      return () => clearTimeout(timer);
    }
  }, [search, filtered, orderQty]);

  const handleItemClick = useCallback((item) => {
    const cur = orderQty[orderKeyOf(item)] || 0;
    if (cur === 0) setQty(orderKeyOf(item), 1);
    else scrollToRow(orderKeyOf(item));
  }, [orderQty, setQty]);

  const orderedItems = useMemo(() =>
    rawMaterials.filter(p => (orderQty[orderKeyOf(p)] || 0) > 0),
    [rawMaterials, orderQty]
  );

  // ── 업체별 그룹 + 신선/공산 sub-그룹
  const vendorGroups = useMemo(() => {
    const groups = {};
    orderedItems.forEach(item => {
      const v = item.vendor?.trim() || '미분류';
      if (!groups[v]) groups[v] = [];
      groups[v].push(item);
    });
    const sorted = Object.keys(groups).sort((a, b) =>
      a === '미분류' ? 1 : b === '미분류' ? -1 : a.localeCompare(b, 'ko')
    );
    return sorted.map(v => {
      const items = groups[v];
      const subBuckets = { fresh: [], industrial: [], supplies: [], other: [] };
      for (const it of items) {
        const cat = classifyItem(it);
        (subBuckets[cat] || subBuckets.other).push(it);
      }
      const subList = CATEGORY_ORDER
        .filter(k => subBuckets[k].length > 0)
        .map(k => ({ key: k, meta: CATEGORY_META[k], items: subBuckets[k] }));
      return { vendor: v, items, subGroups: subList };
    });
  }, [orderedItems]);

  const [vendorView, setVendorView] = useState('all'); // 'all' | vendor name
  const [collapsedVendors, setCollapsedVendors] = useState({});

  const toggleVendorCollapse = (v) =>
    setCollapsedVendors(prev => ({ ...prev, [v]: !prev[v] }));

  const displayGroups = vendorView === 'all'
    ? vendorGroups
    : vendorGroups.filter(g => g.vendor === vendorView);

  const totalCost = useMemo(() =>
    orderedItems.reduce((sum, p) => sum + amountOf(p), 0),   // V135
    [orderedItems, orderQty]
  );

  const handleReset = () => {
    const next = {};
    rawMaterials.forEach(p => { next[orderKeyOf(p)] = 0; });
    onOrderQtyChange(prev => ({ ...prev, ...next }));
    if (onOrderNeedChange) onOrderNeedChange(prev => ({ ...prev, ...next }));
  };

  // ── 연락처 관리
  const persistContacts = useCallback((list) => {
    setContacts(list);
    localStorage.setItem('ddabak_contacts', JSON.stringify(list));
  }, []);

  const handleSaveContact = () => {
    if (!cf.name.trim()) { alert(Z('이름을 입력해주세요', '请输入姓名')); return; }
    if (editingContact) {
      persistContacts(contacts.map(c => c.id === editingContact.id ? { ...c, ...cf } : c));
    } else {
      persistContacts([...contacts, { id: String(Date.now()), ...cf }]);
    }
    setShowContactForm(false);
    setEditingContact(null);
    setCf({ name: '', email: '', phone: '' });
  };

  const startEditContact = (contact) => {
    setEditingContact(contact);
    setCf({ name: contact.name, email: contact.email || '', phone: contact.phone || '' });
    setShowContactForm(true);
  };

  const handleDeleteContact = (id) => {
    if (window.confirm(Z('연락처를 삭제하시겠습니까?', '要删除这个联系人吗？'))) {
      persistContacts(contacts.filter(c => c.id !== id));
    }
  };

  // ── 발주서 제목 (사업자명 있으면 사용, 없으면 '발주서')
  const orderTitle = bizName ? `${bizName} ${Z('발주서', '订货单')}` : Z('발주서', '订货单');   // 화면용 (V147 중국어 화면)
  const docTitle = docTitleOf(bizName);                                // 문서용 (V133: 중국어 발주서는 订货单)

  // ── 저장된 발주서 내보내기 (히스토리용)
  const exportHistoryExcel = (entry) => {
    if (!window.XLSX) { alert(Z('Excel 라이브러리 로딩 중입니다. 잠시 후 다시 시도해주세요.', 'Excel 功能还在加载，请稍后再试。')); return; }
    if (lang !== 'zh' && !isPinyinReady()) { ensurePinyin().then(() => exportHistoryExcel(entry)); return; }   // V127
    const title = docTitleOf(entry.bizName);   // V133
    const border = (c='D1D5DB') => ({ top:{style:'thin',color:{rgb:c}}, bottom:{style:'thin',color:{rgb:c}}, left:{style:'thin',color:{rgb:c}}, right:{style:'thin',color:{rgb:c}} });
    const hStyle = (align='center') => ({ font:{bold:true,color:{rgb:'FFFFFF'},sz:11}, fill:{patternType:'solid',fgColor:{rgb:'1E6B3C'}}, alignment:{horizontal:align,vertical:'center'}, border:border('1E6B3C') });
    const dStyle = (odd, align='left') => ({ font:{sz:10,color:{rgb:'374151'}}, fill:{patternType:'solid',fgColor:{rgb:odd?'F0FDF4':'FFFFFF'}}, alignment:{horizontal:align,vertical:'center'}, border:border() });
    const tStyle = (align='left') => ({ font:{bold:true,sz:11,color:{rgb:'1E6B3C'}}, fill:{patternType:'solid',fgColor:{rgb:'DCFCE7'}}, alignment:{horizontal:align,vertical:'center'}, border:{...border('1E6B3C'),top:{style:'medium',color:{rgb:'1E6B3C'}}} });
    // V119: 발주처 열 + 발주처별 정렬
    const headers = ['NO', L('발주처','供应商'), L('재료명','食材名称'), L('규격','规格'), L('단위','单位'), L('발주수량','订货数量')];
    const dataRows = [];
    groupItemsByVendor(entry.items).forEach(g => g.items.forEach(item =>
      dataRows.push([dataRows.length + 1, vendorTitle(g.vendor), docNameXls(item.name), docSpec(item.name, item.spec), docUnit(item.name, item.unit), item.qty])));
    const footerRow = ['', '', L(`총 ${entry.items.length}품목`, `共 ${entry.items.length} 项`), '', '', ''];
    const biLine = bizParts(entry.bizInfo || {}).join(' · ');   // V133
    const aoa = [];
    if (biLine) { aoa.push([biLine,'','','','','']); aoa.push(['','','','','','']); }
    const hr = aoa.length + 1;   // 머리글 행 번호 (사업자 정보가 있으면 3행)
    aoa.push(headers, ...dataRows, footerRow);
    const ws = window.XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{wch:4},{wch:18},{wch:26},{wch:16},{wch:8},{wch:10}];
    ws['!rows'] = [...(biLine ? [{hpx:20},{hpx:8}] : []), {hpx:28}, ...dataRows.map(()=>({hpx:22})), {hpx:26}];
    const COLS = ['A','B','C','D','E','F'];
    const ha = ['center','left','left','left','center','right'];
    COLS.forEach((col,ci) => { const cell=ws[`${col}${hr}`]; if(cell) cell.s=hStyle(ha[ci]); });
    dataRows.forEach((_,ri) => {
      const er=hr+1+ri, odd=ri%2===1;
      COLS.forEach((col,ci) => { const cell=ws[`${col}${er}`]; if(cell){ cell.s=dStyle(odd,ha[ci]); if(ci===1)cell.s.font={...cell.s.font,bold:true,color:{rgb:'1E40AF'}}; if(ci===2)cell.s.font={...cell.s.font,bold:true,color:{rgb:'111827'}}; if(ci===5)cell.s.font={...cell.s.font,bold:true,color:{rgb:'1E40AF'}}; } });
    });
    const fr=hr+dataRows.length+1;
    COLS.forEach((col,ci) => { const cell=ws[`${col}${fr}`]; if(cell) cell.s=tStyle(ha[ci]); });
    const wb = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
    window.XLSX.writeFile(wb, `${title}_${entry.date}.xlsx`);
  };

  const exportHistoryPdf = (entry) => {
    const title = docTitleOf(entry.bizName);   // V133
    // V119: 발주처가 하나여도 발주처별 묶음으로 찍는다
    // V133: 발주 금액은 찍지 않는다 — 업체가 보는 문서라 우리가 사는 값을 알려 주지 않기 위해 (지금 발주서 PDF와 같은 원칙)
    const bodyHtml = printVendorSections(entry.items)
      + `<div style="background:#FAFAFA;border:1px solid #E4E4E7;border-radius:8px;padding:11px 16px;margin-top:8px">`
      + `<span style="font-weight:800;color:#18181B">${L(`총 ${entry.items.length}품목`, `共 ${entry.items.length} 项`)}</span></div>`;
    const html = `<!DOCTYPE html><html lang="ko"><head>
      <meta charset="utf-8"><title>${title}_${entry.date}</title>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;600;700;800&display=swap" rel="stylesheet">
      <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Noto Sans KR',sans-serif;padding:36px 48px;color:#1a1a1a}.hdr{display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:20px}.title{font-size:22px;font-weight:800;color:#18181B}.meta{font-size:12px;color:#6b7280;line-height:1.8}table{width:100%;border-collapse:collapse;margin-bottom:4px}thead th{background:#1A56A0;color:#fff;padding:10px 12px;font-size:12px;font-weight:700}tbody td{padding:9px 12px;font-size:13px;border-bottom:1px solid #f0f0f0}tbody tr:nth-child(even) td{background:#fafafa}tfoot td{background:#FAFAFA;font-weight:800;color:#18181B;padding:10px 12px}@media print{@page{margin:15mm}}${PRINT_GROUP_CSS}</style>
    </head><body>
      <div class="hdr">
        <div><div class="title">📦 ${title}</div>
          <div class="meta">${entry.date}${entry.memo ? ' · ' + entry.memo : ''}</div>
          ${(() => {
            const parts = bizParts(entry.bizInfo || {});   // V133
            return parts.length ? '<div style="font-size:11px;color:#6b7280;margin-top:4px;line-height:1.5">' + parts.map(escHtml).join(' · ') + '</div>' : '';
          })()}
          </div>
        <div class="meta" style="text-align:right">${L(`${entry.items.length}품목`, `共 ${entry.items.length} 项`)}</div>
      </div>
      ${bodyHtml}
    </body></html>`;
    const win = window.open('','_blank');
    if (!win) { alert(Z('팝업이 차단됐습니다. 팝업 허용 후 다시 시도해주세요.', '弹出窗口被拦截了。请允许弹出窗口后再试。')); return; }
    win.document.write(html); win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 900);
  };

  // ── V138: 발주 내역 수량 고치기 ─────────────────────────────────────────
  //    묶음 품목(V135, 감자 10근 묶음)은 묶음 수로 고친다. 0이면 그 품목을 빼고, 다 빼면 발주서를 지울지 묻는다
  const editQtyOf = (item) => {
    if (!hEdit || !historyDetail) return Number(item.qty) || 0;
    const idx = historyDetail.items.indexOf(item);
    if (!Object.prototype.hasOwnProperty.call(hEdit, idx)) return Number(item.qty) || 0;
    const v = Number(hEdit[idx]);
    if (!Number.isFinite(v) || v <= 0) return 0;
    return item.pack > 1 ? Math.round(v * item.pack * 1000) / 1000 : v;
  };
  const saveHistoryEdit = () => {
    if (!historyDetail || !hEdit || !onUpdateOrderHistory) return;
    for (const [k, v] of Object.entries(hEdit)) {
      if (String(v).trim() === '' || !Number.isFinite(Number(v)) || Number(v) < 0) {
        alert(lang === 'zh' ? '请输入正确的数量（0 = 删除该项）' : `수량을 숫자로 넣어 주세요 (0이면 그 품목을 빼요) — ${historyDetail.items[k]?.name || ''}`);
        return;
      }
    }
    const next = applyQtyEdits(historyDetail, hEdit);
    if (next.items.length === 0) {
      if (window.confirm(lang === 'zh' ? '所有食材都删掉了。要删除这张订货单吗？' : '품목을 모두 뺐어요. 이 발주서를 삭제할까요?')) {
        onDeleteOrderHistory(historyDetail.id);
        setHEdit(null); setHistoryDetail(null);
      }
      return;
    }
    onUpdateOrderHistory(next);
    setHistoryDetail(next);
    setHEdit(null);
  };

  // ── V137: 발주 내역 합치기 — 고른 발주서들을 하나로 만들고 원래 것들은 지운다
  const doMerge = (picked) => {
    const chk = canMergeOrders(picked);
    if (!chk.ok || !onMergeOrderHistory) return;
    const merged = mergeOrderEntries(picked);
    const lines = picked.slice().sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0))
      .map(h => `· ${h.date}${savedTimeOf(h) ? ' ' + savedTimeOf(h) : ''} — ${h.items.length}${lang === 'zh' ? '项' : '품목'} · ${money(h.totalCost || 0)}`).join('\n');
    const msg = lang === 'zh'
      ? `把这 ${picked.length} 张订货单合并成一张。\n\n${lines}\n\n→ ${merged.date} · ${merged.items.length}项 · ${money(merged.totalCost)}\n相同食材的数量会相加，原来的 ${picked.length} 张会被删除。\n\n继续吗？`
      : `발주서 ${picked.length}개를 하나로 합칩니다.\n\n${lines}\n\n→ ${merged.date} · ${merged.items.length}품목 · ${money(merged.totalCost)}\n같은 품목은 수량을 더하고, 원래 발주서 ${picked.length}개는 삭제됩니다.\n\n계속할까요?`;
    if (!window.confirm(msg)) return;
    onMergeOrderHistory(picked.map(h => h.id), merged);
    setMergeSel([]);
  };

  // ── V137: 저장한 발주서(내역)를 글로 — 위챗·카톡에 다시 보낼 때 (단가·금액 제외, 문서 언어 규칙은 지금 발주서와 같음)
  const buildEntryText = (entry) => {
    const lines = [`[${docTitleOf(entry.bizName)}] ${entry.date}`];
    const bi = bizParts(entry.bizInfo || {}).join(' · ');
    if (bi) lines.push(bi);
    lines.push('');
    const groups = groupItemsByVendor(entry.items || []);
    const multi = groups.length > 1;
    let n = 0;
    groups.forEach((g, gi) => {
      if (multi || g.vendor !== NO_VENDOR) { if (gi > 0) lines.push(''); lines.push(`▶ ${vendorTitle(g.vendor)}`); }
      g.items.forEach(it => {
        n++;
        const sp = it.spec && it.spec !== '-' ? ` (${docSpec(it.name, it.spec)})` : '';
        lines.push(`${multi ? '  ' : ''}${n}. ${docNameText(it.name)}${sp}  ${fmtQty(it.qty)}${docUnit(it.name, it.unit)}`);
      });
    });
    lines.push('');
    lines.push(L(`총 ${(entry.items || []).length}품목`, `共 ${(entry.items || []).length} 项`));
    return lines.join('\n');
  };
  // V139: 내역 상세에서 발주처를 골라 두면 그 발주처 품목만 내보낸다 (예전엔 골라도 발주서 전체가 복사됨)
  const scopedDetail = (entry) => {
    if (!entry || hVendorFilter === 'all') return entry;
    const items = (entry.items || []).filter(i => (String(i.vendor || '').trim() || NO_VENDOR) === hVendorFilter);
    if (!items.length) return entry;
    return { ...entry, vendor: hVendorFilter, items,
      totalCost: Math.round(items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.price) || 0), 0) * 100) / 100 };
  };
  const copyEntryText = async (entry) => {
    const text = buildEntryText(entry);
    if (isMobile && typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ text }); setEntryCopied('shared'); return; }
      catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(text); setEntryCopied('copied'); }
    catch (e) { setEntryCopied(''); alert(text); }
  };

  // ── 발주서 텍스트 빌드 (외부 전송용 — 단가·금액 제외)
  const buildOrderText = useCallback(() => {
    const today = getToday();
    const lines = [`[${docTitle}] ${today}`];
    if (bizInfoText) lines.push(bizInfoText);
    lines.push('');

    const multiVendor = vendorGroups.length > 1;
    let counter = 0;
    vendorGroups.forEach((group, vi) => {
      // V119: 발주처가 하나여도 이름은 찍는다 (발주처 없는 품목만 있을 때는 생략)
      if (multiVendor || group.vendor !== NO_VENDOR) {
        if (vi > 0) lines.push('');
        lines.push(`▶ ${vendorTitle(group.vendor)}`);
      }
      const hasSubGroups = group.subGroups && group.subGroups.length >= 1;  // V81: 항상
      const formatItem = (item, indent = '') => {
        counter++;
        const qty = fmtQty(docQtyOf(item));   // V135 실제 양
        const eff = getEffSpec(item);
        const spec = eff && eff !== '-' ? ` (${docSpec(item.name, eff)})` : '';
        lines.push(`${indent}${counter}. ${docNameText(item.name)}${spec}  ${qty}${docUnit(item.name, item.unit)}`);
        const href = safeLinkHref(item.link);   // V118: 온라인 구매 품목은 링크도 같이
        if (href) lines.push(`${indent}   🔗 ${href}`);
      };
      if (hasSubGroups) {
        group.subGroups.forEach(sg => {
          lines.push(`${multiVendor ? '  ' : ''}[${cnDoc ? tr(sg.meta.label, 'zh') : sg.meta.label}]`);
          sg.items.forEach(item => formatItem(item, multiVendor ? '  ' : ''));
        });
      } else {
        group.items.forEach(item => formatItem(item, multiVendor ? '  ' : ''));
      }
    });

    lines.push('');
    lines.push(L(`총 ${orderedItems.length}품목`, `共 ${orderedItems.length} 项`));
    return lines.join('\n');
  }, [orderedItems, orderQty, docTitle, vendorGroups, bizInfoText, specExclude, vendorZhMap, biPrint, zhMap, prices]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 발주서 HTML 표 빌드 (외부 전송용 — 단가·금액 제외)
  const buildOrderHtml = useCallback(() => {
    const today = getToday();
    // V119: 발주처마다 머리줄을 넣고 번호를 새로 매긴다
    const rowsHtml = groupItemsByVendor(orderedItems).map(g => {
      const head = `<tr><td colspan="4" style="padding:9px 12px;background:#1e40af;color:#fff;font-weight:800;font-size:13px;border:1px solid #1e40af">`
        + `🏪 ${escHtml(vendorTitle(g.vendor))} <span style="font-weight:400;font-size:11px;opacity:.85">${L(`${g.items.length}품목`, `${g.items.length}项`)}</span></td></tr>`;
      return head + g.items.map((item, i) => {
        const qty = docQtyOf(item);   // V135 실제 양
        const bg = i % 2 === 0 ? '#ffffff' : '#FAFAFA';
        return `<tr style="background:${bg}">
        <td style="padding:8px 12px;text-align:center;color:#9ca3af;font-size:12px;border:1px solid #e5e7eb">${i+1}</td>
        <td style="padding:8px 12px;font-weight:700;color:#111827;border:1px solid #e5e7eb">${docNameHtml(item.name)}${linkHtml(item.name)}</td>
        <td style="padding:8px 12px;color:#6b7280;border:1px solid #e5e7eb">${specHtml(item.name, getEffSpec(item))}</td>
        <td style="padding:8px 12px;text-align:right;font-weight:700;color:#1e40af;border:1px solid #e5e7eb">${qtyHtml(item.name, qty, item.unit)}</td>
      </tr>`;
      }).join('');
    }).join('');
    return `<table style="border-collapse:collapse;width:100%;font-family:'Malgun Gothic',sans-serif;font-size:13px">
      <caption style="text-align:left;font-size:15px;font-weight:800;color:#18181B;padding-bottom:10px;caption-side:top">
        ${escHtml(docTitle)} &nbsp;<span style="font-size:12px;font-weight:400;color:#9ca3af">${today}</span>${bizInfoHtml}
      </caption>
      <thead>
        <tr style="background:#1A56A0">
          <th style="padding:10px 12px;color:#fff;font-size:11px;border:1px solid #1A56A0;width:36px">NO</th>
          <th style="padding:10px 12px;color:#fff;font-size:11px;text-align:left;border:1px solid #1A56A0">${L('재료명', '食材名称')}</th>
          <th style="padding:10px 12px;color:#fff;font-size:11px;text-align:left;border:1px solid #1A56A0">${L('규격', '规格')}</th>
          <th style="padding:10px 12px;color:#fff;font-size:11px;text-align:right;border:1px solid #1A56A0">${L('발주 수량', '订货数量')}</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
      <tfoot>
        <tr style="background:#F4F4F5;border-top:2px solid #1A56A0">
          <td colspan="4" style="padding:11px 12px;font-weight:800;color:#18181B;border:1px solid #e5e7eb;font-size:13px">
            ${L(`총 ${orderedItems.length}품목`, `共 ${orderedItems.length} 项`)}
          </td>
        </tr>
      </tfoot>
    </table>`;
  }, [orderedItems, orderQty, docTitle, bizInfoHtml, vendorZhMap, biPrint, prices, specExclude, lang, pyReady, zhMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 엑셀 다운로드 (스타일 적용)
  const handleExcelDownload = () => {
    if (!window.XLSX) { alert(Z('Excel 라이브러리 로딩 중입니다. 잠시 후 다시 시도해주세요.', 'Excel 功能还在加载，请稍后再试。')); return; }
    if (lang !== 'zh' && !isPinyinReady()) { ensurePinyin().then(() => handleExcelDownload()); return; }   // V127
    const today = getToday();

    // 스타일 헬퍼
    const border = (color = 'D1D5DB') => ({
      top:    { style: 'thin', color: { rgb: color } },
      bottom: { style: 'thin', color: { rgb: color } },
      left:   { style: 'thin', color: { rgb: color } },
      right:  { style: 'thin', color: { rgb: color } },
    });

    const headerStyle = (align = 'center') => ({
      font:      { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
      fill:      { patternType: 'solid', fgColor: { rgb: '1E6B3C' } },
      alignment: { horizontal: align, vertical: 'center' },
      border:    border('1E6B3C'),
    });

    const dataStyle = (isOdd, align = 'left') => ({
      font:      { sz: 10, color: { rgb: '374151' } },
      fill:      { patternType: 'solid', fgColor: { rgb: isOdd ? 'F0FDF4' : 'FFFFFF' } },
      alignment: { horizontal: align, vertical: 'center' },
      border:    border(),
    });

    const totalStyle = (align = 'left') => ({
      font:      { bold: true, sz: 11, color: { rgb: '1E6B3C' } },
      fill:      { patternType: 'solid', fgColor: { rgb: 'DCFCE7' } },
      alignment: { horizontal: align, vertical: 'center' },
      border:    { ...border('1E6B3C'), top: { style: 'medium', color: { rgb: '1E6B3C' } } },
    });

    // AOA 데이터 (단가·금액 제외) — V119: 발주처 열 + 발주처별 정렬
    const headers = ['NO', L('발주처', '供应商'), L('재료명', '食材名称'), L('규격', '规格'), L('단위', '单位'), L('발주수량', '订货数量')];
    const dataRows = [];
    groupItemsByVendor(orderedItems).forEach(g => g.items.forEach(item => dataRows.push([
      dataRows.length + 1,
      vendorTitle(g.vendor),
      docNameXls(item.name),   // V127 발음 / V133 중국어 발주서는 중국어
      docSpec(item.name, getEffSpec(item)),   // V131·V133 규격·단위
      docUnit(item.name, item.unit),
      docQtyOf(item),   // V135 실제 양
    ])));
    const footerRow = ['', '', L(`총 ${orderedItems.length}품목`, `共 ${orderedItems.length} 项`), '', '', ''];
    const aoa = [];
    // 사업자 정보 (있을 때만 시트 상단에 추가)
    if (bizInfoText) {
      aoa.push([bizInfoText, '', '', '', '', '']);
      aoa.push(['', '', '', '', '', '']);  // 빈 행 (구분용)
    }
    const headRow = aoa.length + 1;   // 머리글 행 번호 — 예전엔 사업자 정보가 있어도 1행에 스타일을 걸어 어긋났다
    aoa.push(headers, ...dataRows, footerRow);

    const ws = window.XLSX.utils.aoa_to_sheet(aoa);

    // 열 너비 · 행 높이 (6열)
    const COLS6 = ['A','B','C','D','E','F'];
    ws['!cols'] = [{wch:4},{wch:18},{wch:26},{wch:16},{wch:8},{wch:10}];
    ws['!rows'] = [...(bizInfoText ? [{ hpx: 20 }, { hpx: 8 }] : []), { hpx: 28 }, ...dataRows.map(() => ({ hpx: 22 })), { hpx: 26 }];

    const aligns = ['center','left','left','left','center','right'];
    // 헤더 행 스타일
    COLS6.forEach((col, ci) => {
      const cell = ws[`${col}${headRow}`];
      if (cell) cell.s = headerStyle(aligns[ci]);
    });

    // 데이터 행 스타일
    dataRows.forEach((_, ri) => {
      const excelRow = headRow + 1 + ri;
      const isOdd = ri % 2 === 1;
      COLS6.forEach((col, ci) => {
        const cell = ws[`${col}${excelRow}`];
        if (cell) {
          cell.s = dataStyle(isOdd, aligns[ci]);
          if (ci === 0) cell.s.font = { ...cell.s.font, bold: true, color: { rgb: '9CA3AF' } };
          if (ci === 1) cell.s.font = { ...cell.s.font, bold: true, color: { rgb: '1E40AF' } };
          if (ci === 2) cell.s.font = { ...cell.s.font, bold: true, color: { rgb: '111827' } };
          if (ci === 5) cell.s.font = { ...cell.s.font, bold: true, color: { rgb: '1E40AF' } };
        }
      });
    });

    // 합계 행 스타일
    const footerRow_n = headRow + dataRows.length + 1;
    COLS6.forEach((col, ci) => {
      const cell = ws[`${col}${footerRow_n}`];
      if (cell) cell.s = totalStyle(aligns[ci]);
    });

    const wb = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(wb, ws, docTitle.slice(0, 31));
    window.XLSX.writeFile(wb, `${docTitle}_${today}.xlsx`);
  };

  // ── PDF 저장 (단가·금액 제외)
  const handlePdfDownload = () => {
    const today = getToday();
    // V119: 발주처별 묶음 (발주처가 하나여도 이름을 찍는다)
    const rows = orderedItems.map(item => ({
      name: item.name, vendor: item.vendor, unit: item.unit,
      spec: getEffSpec(item) || '-', qty: docQtyOf(item),   // V135 실제 양
    }));
    const vendorCount = groupItemsByVendor(rows).length;

    const html = `<!DOCTYPE html><html lang="ko"><head>
      <meta charset="utf-8"><title>${docTitle}_${today}</title>
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;600;700;800&display=swap" rel="stylesheet">
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Noto Sans KR',sans-serif;padding:40px 48px;color:#1a1a1a}
        .hdr{display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:20px}
        .title{font-size:22px;font-weight:800;color:#FF6F0F}
        .date{font-size:13px;color:#6b7280}
        .summary{font-size:13px;color:#374151;margin-bottom:18px}
        table{width:100%;border-collapse:collapse}
        thead th{background:#FF6F0F;color:#fff;padding:10px 12px;font-size:12px;font-weight:700}
        tbody td{padding:9px 12px;font-size:13px;border-bottom:1px solid #f0f0f0}
        tbody tr:nth-child(even) td{background:#fafafa}
        tfoot td{background:#FAFAFA;font-weight:800;color:#FF6F0F;padding:11px 12px}
        .total{background:#FAFAFA;font-weight:800;color:#FF6F0F;padding:11px 12px;font-size:14px;border-radius:6px}
        @media print{@page{margin:15mm}}
        ${PRINT_GROUP_CSS}
      </style>
    </head><body>
      <div class="hdr"><span class="title">📦 ${escHtml(docTitle)}</span><span class="date">${today}</span></div>
      ${bizInfoText ? '<div style="font-size:11px;color:#6b7280;margin:6px 0 14px">' + bizInfoText + '</div>' : ''}
      <div class="summary">${L(`${orderedItems.length}개 품목 · 발주처 ${vendorCount}곳`, `${orderedItems.length}项 · 供应商${vendorCount}家`)}</div>
      ${printVendorSections(rows)}
      <div class="total">${L(`총 ${orderedItems.length}품목`, `共 ${orderedItems.length} 项`)}</div>
    </body></html>`;

    const win = window.open('', '_blank');
    if (!win) { alert(Z('팝업이 차단됐습니다. 팝업 허용 후 다시 시도해주세요.', '弹出窗口被拦截了。请允许弹出窗口后再试。')); return; }
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 900);
  };

  // ── 이메일 → 미리보기 모달 열기
  const handleEmailSend = (contact) => {
    setPreview({ mode: 'email', contact });
    setCopied(false);
  };

  // ── 문자 → 미리보기 모달 열기
  const handleSmsSend = (contact) => {
    setPreview({ mode: 'sms', contact });
    setCopied(false);
  };

  // ── 클립보드 복사 (HTML + 텍스트)
  const handleCopyToClipboard = async (mode) => {
    try {
      if (mode === 'email') {
        // HTML 표 + 텍스트 fallback 동시 복사
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html':  new Blob([buildOrderHtml()], { type: 'text/html' }),
            'text/plain': new Blob([buildOrderText()], { type: 'text/plain' }),
          })
        ]);
      } else {
        await navigator.clipboard.writeText(buildOrderText());
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback
      navigator.clipboard.writeText(buildOrderText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // ── 이메일/문자 앱 열기
  const handleOpenApp = () => {
    if (!preview) return;
    const today = getToday();
    if (preview.mode === 'email') {
      window.location.href =
        `mailto:${preview.contact.email}?subject=${encodeURIComponent(`${L('[발주서]', '[订货单]')} ${today}`)}&body=${encodeURIComponent(buildOrderText())}`;
    } else {
      window.location.href = `sms:${preview.contact.phone}?body=${encodeURIComponent(buildOrderText())}`;
    }
  };

  // ── V133: 위챗 발주 — 위챗은 웹에서 바로 보내는 공개 기능이 없다. 중국어 발주서 '글'을 만들어
  //    휴대폰은 공유창(위챗 선택), 컴퓨터는 클립보드에 복사 → 위챗 대화창에 붙여넣기
  const [wechatState, setWechatState] = useState('');   // '' | 'copied' | 'shared' | 'fail'
  const handleWechatSend = async () => {
    const text = buildOrderText();
    if (isMobile && typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ text }); setWechatState('shared'); return; }
      catch (e) { if (e && e.name === 'AbortError') return; /* 공유창 실패 → 복사로 */ }
    }
    try { await navigator.clipboard.writeText(text); setWechatState('copied'); }
    catch (e) { setWechatState('fail'); }
  };

  // ── 카카오 전송
  const handleKakaoSend = () => {
    if (!window.Kakao) { alert('카카오 SDK를 불러오지 못했습니다.'); return; }
    if (!window.Kakao.isInitialized()) {
      alert('카카오 앱 키 설정이 필요합니다.\n.env 파일에 REACT_APP_KAKAO_KEY를 추가해주세요.');
      return;
    }
    const today = getToday();
    const MAX = 5;
    const previewLines = orderedItems.slice(0, MAX)
      .map(item => `• ${item.name}${zhInline(item.name)} ${fmtQty(docQtyOf(item))}${unitText(item.name, item.unit)}`).join('\n');
    const more = orderedItems.length > MAX ? `\n... 외 ${orderedItems.length - MAX}건` : '';
    window.Kakao.Share.sendDefault({
      objectType: 'text',
      text: `[${orderTitle}] ${today}\n\n${previewLines}${more}\n\n총 ${orderedItems.length}품목`,
      link: { mobileWebUrl: window.location.href, webUrl: window.location.href },
    });
  };

  // ── 공유 모달 닫기
  const closeShareModal = () => {
    setShowShareModal(false);
    setShowContactForm(false);
    setEditingContact(null);
    setCf({ name: '', email: '', phone: '' });
  };

  const btnBase = {
    padding: '8px 14px', borderRadius: '8px', fontSize: '12px',
    fontWeight: '700', cursor: 'pointer', border: 'none',
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── 모바일 패널 탭 ── */}
      {isMobile && (
        <div style={{ display: 'flex', background: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <button onClick={() => setMobilePanelTab('list')}
            style={{
              flex: 1, padding: '12px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '700',
              background: mobilePanelTab === 'list' ? '#FAFAFA' : '#fff',
              color: mobilePanelTab === 'list' ? '#FF6F0F' : '#9ca3af',
              borderBottom: mobilePanelTab === 'list' ? '2px solid #FF6F0F' : '2px solid transparent',
            }}>
            🔍 {t('원재료 목록')} ({rawMaterials.length})
          </button>
          <button onClick={() => setMobilePanelTab('order')}
            style={{
              flex: 1, padding: '12px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '700',
              background: mobilePanelTab === 'order' ? '#FAFAFA' : '#fff',
              color: mobilePanelTab === 'order' ? '#FF6F0F' : '#9ca3af',
              borderBottom: mobilePanelTab === 'order' ? '2px solid #FF6F0F' : '2px solid transparent',
            }}>
            📦 {t('발주서')} {orderedItems.length > 0 && <span style={{ color: '#FF8F00' }}>({orderedItems.length})</span>}
          </button>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── 왼쪽: 원재료 목록 ── */}
      <div ref={leftPanelRef} style={{
        // V134: 320px로는 재료명이 "녹색 와…"처럼 잘려서 발주를 못 했다. 화면이 넓으면 더 넓게 (오른쪽 발주서 칸은 여유가 많음)
        //       가운데 막대를 끌면 원하는 폭으로 (leftW)
        width: isMobile ? '100%' : (leftW ? `${leftW}px` : 'clamp(360px, 42vw, 560px)'),
        minWidth: isMobile ? '100%' : '280px',
        borderRight: isMobile ? 'none' : '1px solid #e5e7eb',
        display: isMobile && mobilePanelTab !== 'list' ? 'none' : 'flex',
        flexDirection: 'column', background: '#fff', flexShrink: 0,
      }}>
        <div style={{ padding: '14px 12px 10px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: '14px', fontWeight: '800', color: '#18181B', marginBottom: '8px' }}>
            {t('원재료 목록')} <span style={{ fontSize: '11px', fontWeight: '400', color: '#9ca3af' }}>({rawMaterials.length}{Z('종', '种')})</span>
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={Z('🔍 재료명 검색...', '🔍 搜索食材...')}
            style={{ width: '100%', padding: '9px 12px', boxSizing: 'border-box',
                     border: '1.5px solid #FF6F0F', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
          {search && (
            <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '5px' }}>
              {lang === 'zh'
                ? <>{filtered.length} 项 · <span style={{ color: '#18181B', fontWeight: '700' }}>点名称</span>就加到订货单</>
                : <>{filtered.length}개 · <span style={{ color: '#18181B', fontWeight: '700' }}>이름 클릭</span>하면 발주서에 추가</>}
            </p>
          )}
          {/* V92: 숨김 품목 토글 */}
          {hiddenCount > 0 && (
            <div style={{ marginTop: 6 }}>
              <button onClick={() => setShowHidden(s => !s)}
                style={{
                  padding: '4px 10px', fontSize: 11, fontWeight: 700,
                  background: showHidden ? '#18181B' : '#fff',
                  color: showHidden ? '#fff' : '#52525B',
                  border: '1.5px solid ' + (showHidden ? '#18181B' : '#D4D4D8'),
                  borderRadius: 6, cursor: 'pointer',
                }}>
                {showHidden ? Z(`✕ 숨김 ${hiddenCount}개 닫기`, `✕ 收起已隐藏的 ${hiddenCount} 项`) : Z(`숨김 ${hiddenCount}개 보기`, `查看已隐藏的 ${hiddenCount} 项`)}
              </button>
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
          {filtered.length === 0 && (
            <p style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0', fontSize: '13px' }}>{t('검색 결과 없음')}</p>
          )}
          {filtered.map((item, idx) => {
            const qty = qtyOf(item);
            const safety = Number(item.safetyStock) || 0;
            // V93: vendor/category 변경 시 헤더 삽입
            const curVendor = (item.vendor || '').trim() || '거래처 미지정';
            const curCat = resolveCat(item);
            const prev = idx === 0 ? null : filtered[idx - 1];
            const prevVendor = prev ? ((prev.vendor || '').trim() || '거래처 미지정') : null;
            const prevCat = prev ? resolveCat(prev) : null;
            const isNewVendor = curVendor !== prevVendor;
            const isNewCat = isNewVendor || curCat !== prevCat;
            return (
              <React.Fragment key={item._idx}>
              {isNewVendor && (
                <div style={{
                  marginTop: idx === 0 ? 0 : 14,
                  marginBottom: 6,
                  padding: '8px 10px',
                  background: '#18181B',
                  color: '#fff',
                  fontSize: 13, fontWeight: 800,
                  borderRadius: 6, letterSpacing: '-0.01em',
                }}>
                  {curVendor === '거래처 미지정' ? t('🏷 거래처 미지정') : vendorLabel(curVendor)}
                </div>
              )}
              {isNewCat && (
                <div style={{
                  marginTop: isNewVendor ? 4 : 8,
                  marginBottom: 4,
                  padding: '4px 8px',
                  fontSize: 11, fontWeight: 700,
                  color: '#52525B',
                  letterSpacing: '-0.01em',
                }}>
                  ▸ {t(curCat)}
                </div>
              )}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 10px', marginBottom: '4px', borderRadius: '8px',
                border: `1.5px solid ${qty > 0 ? '#FF6F0F' : '#f0f0f0'}`,
                background: qty > 0 ? '#FAFAFA' : '#fff',
              }}>
                <button onClick={() => handleItemClick(item)}
                  style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0, minWidth: 0 }}>
                  {/* V134: 재료명이 길면 잘라 '…' 하지 않고 줄을 바꿔 전부 보여준다 */}
                  <div style={{ fontSize: '13px', fontWeight: qty > 0 ? '700' : '500',
                                color: qty > 0 ? '#FF6F0F' : '#374151',
                                whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
                    {qty > 0 && '✓ '}<BiName ko={item.name} zhMap={zhMap} />
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>
                    {specText(item.name, item.spec)} · {priceLabel(item)}
                  </div>
                </button>
                {safeLinkHref(item.link) && (
                  <a href={safeLinkHref(item.link)} target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()} title={`${t('클릭해서 구매 페이지 열기')} · ${linkLabel(item.link)}`}
                    style={{ flexShrink: 0, fontSize: 15, textDecoration: 'none', padding: '2px 4px' }}>🔗</a>
                )}
                {/* V91: 안전재고 인라인 편집 */}
                <div style={{
                  flexShrink: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', gap: 2,
                  padding: '4px 8px', borderRight: '1px solid #E4E4E7',
                  marginRight: 4,
                }} onClick={e => e.stopPropagation()}>
                  <div style={{ fontSize: 10, color: '#71717A', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {Z('안전재고', '安全库存')}
                  </div>
                  <input
                    type="number"
                    value={item.safetyStock != null ? item.safetyStock : ''}
                    onChange={e => updateSafetyStock(item, e.target.value)}
                    placeholder="0"
                    title={Z('안전재고 수량 — 이 수량 아래로 떨어지면 발주 필요', '安全库存 — 低于这个数量就提醒订货')}
                    style={{
                      width: 50, padding: '4px 6px',
                      fontSize: 13, fontWeight: 700, textAlign: 'center',
                      color: safety > 0 ? '#18181B' : '#A1A1AA',
                      border: '1.5px solid ' + (safety > 0 ? '#1A56A0' : '#E4E4E7'),
                      borderRadius: 4, outline: 'none', background: '#fff',
                    }}
                  />
                </div>
                <div style={{ flexShrink: 0, textAlign: 'center' }}>
                  <QtyInput value={qty} onChange={v => setQty(keyOf(item), v)} min={0} size="sm" />
                  {packOf(item).pack > 1 && (
                    <div title={t('수량 1 = 규격만큼')} style={{ fontSize: 10, fontWeight: 700, color: '#1A56A0', marginTop: 2, whiteSpace: 'nowrap' }}>
                      {qty > 0 ? `= ${fmtQty(docQtyOf(item))}${unitShow(item)}` : `1 = ${fmtQty(packOf(item).pack)}${unitShow(item)}`}
                    </div>
                  )}
                </div>
                {/* V92: 감추기/다시보기 버튼 */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleHiddenInOrder(item); }}
                  title={item.hiddenInOrder ? Z('발주서 목록에 다시 표시', '重新显示在订货列表') : Z('발주서 목록에서 감추기', '从订货列表隐藏')}
                  style={{
                    flexShrink: 0, padding: '6px 8px',
                    background: item.hiddenInOrder ? '#18181B' : '#fff',
                    color: item.hiddenInOrder ? '#fff' : '#A1A1AA',
                    border: '1.5px solid ' + (item.hiddenInOrder ? '#18181B' : '#E4E4E7'),
                    borderRadius: 6, cursor: 'pointer',
                    fontSize: 11, fontWeight: 700, lineHeight: 1,
                    minWidth: 32,
                  }}>
                  {item.hiddenInOrder ? '↺' : '✕'}
                </button>
              </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* V134: 폭 조절 막대 (컴퓨터만) */}
      {!isMobile && (
        <div
          role="separator" aria-orientation="vertical"
          title={Z('끌어서 목록 폭 조절 · 두 번 누르면 기본 폭', '拖动调整宽度，双击恢复')}
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onPointerCancel={() => setResizing(false)}
          onDoubleClick={resetLeftW}
          style={{
            width: 8, flexShrink: 0, cursor: 'col-resize', touchAction: 'none', userSelect: 'none',
            background: resizing ? '#FDBA74' : 'transparent', marginLeft: -4, marginRight: -4, zIndex: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
          }}>
          <div style={{ width: 3, height: 36, borderRadius: 2, background: resizing ? '#EA580C' : '#D4D4D8' }} />
        </div>
      )}

      {/* ── 오른쪽: 발주서 ── */}
      <div style={{
        flex: 1, display: isMobile && mobilePanelTab !== 'order' ? 'none' : 'flex',
        flexDirection: 'column', background: '#f8fafb', overflow: 'hidden',
      }}>

        {/* 헤더 */}
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', gap: '10px',
          background: '#fff', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1 }}>
            {/* 사업자명 편집 */}
            {editingBizName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <input
                  autoFocus
                  value={bizNameDraft}
                  onChange={e => setBizNameDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveBizName(); if (e.key === 'Escape') setEditingBizName(false); }}
                  placeholder={Z('사업자 이름 입력', '输入门店名称')}
                  style={{ fontSize: '14px', fontWeight: '700', padding: '4px 8px', border: '1.5px solid #FF6F0F',
                           borderRadius: '6px', outline: 'none', width: '160px', color: '#18181B' }}
                />
                <button onClick={saveBizName}
                  style={{ padding: '4px 10px', borderRadius: '6px', border: 'none',
                           background: '#FF6F0F', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                  {t('저장')}
                </button>
                <button onClick={() => setEditingBizName(false)}
                  style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #e5e7eb',
                           background: '#fff', color: '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                  {t('취소')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                <div style={{ fontSize: '16px', fontWeight: '800', color: '#18181B' }}>
                  📦 {orderTitle}
                </div>
                <button onClick={() => { setBizNameDraft(bizName); setEditingBizName(true); }}
                  style={{ padding: '2px 8px', borderRadius: '5px', border: '1px solid #d1d5db',
                           background: '#f9fafb', color: '#6b7280', fontSize: '11px', cursor: 'pointer' }}>
                  ✏️ {bizName ? Z('이름 변경', '修改名称') : Z('사업자명 설정', '设置门店名称')}
                </button>
                <button onClick={() => setShowBizModal(true)}
                  title={Z('사업자등록번호·대표·연락처·주소', '营业执照号·负责人·电话·地址')}
                  style={{ padding: '2px 8px', borderRadius: '5px', border: '1px solid #FF6F0F',
                           background: '#FAFAFA', color: '#18181B', fontSize: '11px', cursor: 'pointer', fontWeight: 700 }}>
                  📋 {Z('상세 정보', '详细信息')}
                </button>
                {(bizInfo.regNum || bizInfo.ceo || bizInfo.phone || bizInfo.address) && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginLeft: 8, lineHeight: 1.5 }}>
                    {[bizInfo.ceo && Z('대표 ', '负责人 ') + bizInfo.ceo, bizInfo.regNum, bizInfo.phone && '📞 ' + bizInfo.phone, bizInfo.address].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            )}
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {Z(`${orderedItems.length}개 품목 · 합계 `, `${orderedItems.length} 项 · 合计 `)}{money(totalCost)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {orderedItems.length > 0 && (
              <button onClick={handleReset}
                style={{ ...btnBase, border: '1.5px solid #E4E4E7', background: '#fff5f5', color: '#52525B' }}>
                {t('초기화')}
              </button>
            )}
            <button onClick={() => setShowHistoryModal(true)}
              style={{ ...btnBase, background: '#FAFAFA', color: '#18181B', border: '1px solid #F4F4F5',
                       position: 'relative' }}>
              📅 {Z('내역', '记录')}{orderHistory.length > 0 && (
                <span style={{ marginLeft: '4px', background: '#FF6F0F', color: '#fff',
                               borderRadius: '10px', fontSize: '10px', padding: '1px 5px', fontWeight: '800' }}>
                  {orderHistory.length}
                </span>
              )}
            </button>
            {orderedItems.length > 0 && (
              <button onClick={handleSaveOrder}
                style={{ ...btnBase, background: savedFlash ? '#6b7280' : '#FAFAFA',
                         color: savedFlash ? '#fff' : '#FF6F0F',
                         border: `1.5px solid ${savedFlash ? '#6b7280' : '#FF6F0F'}` }}>
                {savedFlash ? Z('✅ 저장됨!', '✅ 已保存！') : Z('💾 저장', '💾 保存')}
              </button>
            )}
            {orderedItems.length > 0 && (
              <button onClick={() => { setShowShareModal(true); setShareTab(cnDoc ? 'send' : 'save'); setWechatState(''); }}
                style={{ ...btnBase, background: '#FAFAFA', color: '#18181B', border: '1.5px solid #FF6F0F' }}>
                📤 {Z('전송', '发送')}
              </button>
            )}
            {!isMobile && (
              <button onClick={() => window.print()}
                style={{ ...btnBase, background: '#FF6F0F', color: '#fff' }}>
                🖨️ {Z('인쇄', '打印')}
              </button>
            )}
          </div>
        </div>

        {/* 업체 필터 탭 */}
        {orderedItems.length > 0 && vendorGroups.length > 1 && (
          <div style={{ padding: '0 20px 0', display: 'flex', gap: '6px', flexWrap: 'wrap',
                        background: '#fff', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px', paddingTop: '10px' }}>
            <button onClick={() => setVendorView('all')}
              style={{ ...btnBase, fontSize: '12px', padding: '5px 12px',
                       background: vendorView === 'all' ? '#FF6F0F' : '#f3f4f6',
                       color: vendorView === 'all' ? '#fff' : '#6b7280', border: 'none' }}>
              {Z('전체', '全部')} ({orderedItems.length})
            </button>
            {vendorGroups.map(g => (
              <button key={g.vendor} onClick={() => setVendorView(g.vendor)}
                style={{ ...btnBase, fontSize: '12px', padding: '5px 12px',
                         background: vendorView === g.vendor ? '#1e40af' : '#f3f4f6',
                         color: vendorView === g.vendor ? '#fff' : '#374151', border: 'none' }}>
                {vendorLabel(g.vendor)} ({g.items.length})
              </button>
            ))}
          </div>
        )}

        {/* 테이블 */}
        <div ref={rightPanelRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {savedNote && (
            <div role="status" data-saved-note style={{
              marginBottom: 14, padding: '11px 14px', borderRadius: 10, background: '#F0FDF4', border: '1.5px solid #86EFAC',
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13, color: '#14532D',
            }}>
              <div style={{ flex: 1, minWidth: 220, lineHeight: 1.55 }}>
                <div style={{ fontWeight: 800 }}>✅ {t('발주서를 저장하고 비웠어요')} · {savedNote.date} · {money(savedNote.total || 0)}</div>
                <div style={{ fontSize: 12, margin: '2px 0' }}>
                  {savedNote.list.map(x => `📄 ${vendorLabel(x.vendor === NO_VENDOR ? '' : x.vendor) || t('발주처 미지정')} ${x.count}${t('품목')}`).join('  ·  ')}
                </div>
                <div style={{ fontSize: 12 }}>{t('추가 발주는 새로 담아서 저장하면 따로 저장돼요. 하나로 묶으려면 📅 내역에서 골라 🔗 합치기.')}</div>
              </div>
              <button onClick={() => { setShowHistoryModal(true); setSavedNote(null); }}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #86EFAC', background: '#fff', color: '#14532D', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                📅 {t('내역 보기')}
              </button>
              <button onClick={() => setSavedNote(null)} aria-label="close"
                style={{ background: 'none', border: 'none', color: '#4D7C0F', fontSize: 16, cursor: 'pointer', padding: '0 4px' }}>✕</button>
            </div>
          )}
          {showCnBanner && (
            <div role="note" data-cn-banner style={{
              marginBottom: 14, padding: '12px 14px', borderRadius: 10,
              background: '#FFF7ED', border: '1.5px solid #FDBA74',
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 220, fontSize: 13, color: '#7C2D12', lineHeight: 1.55 }}>
                <div style={{ fontWeight: 800 }}>🌏 {t('이 매장의 국가가 ‘한국’으로 되어 있어요')}</div>
                <div style={{ fontSize: 12, marginTop: 2 }}>{t('그래서 금액이 원으로 나오고, 수량 1 = 규격만큼(예: 10근) 발주와 중국어 발주서가 꺼져 있어요. 중국 매장이면 바꿔 주세요.')}</div>
              </div>
              <button onClick={switchToChina} disabled={switchingCountry}
                style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: '#EA580C', color: '#fff',
                         fontSize: 13, fontWeight: 800, cursor: switchingCountry ? 'default' : 'pointer', opacity: switchingCountry ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                🇨🇳 {t('중국 매장으로 바꾸기')}
              </button>
              <button onClick={hideCnBanner}
                style={{ padding: '9px 10px', borderRadius: 8, border: '1px solid #FED7AA', background: '#fff', color: '#9A3412',
                         fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {t('나중에')}
              </button>
            </div>
          )}
          {orderedItems.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                          justifyContent: 'center', height: '80%', color: '#9ca3af' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📦</div>
              <p style={{ fontSize: '14px', fontWeight: '600' }}>{Z('왼쪽에서 재료를 검색하고 추가하세요', '在左边搜索食材，点一下就加进来')}</p>
              <p style={{ fontSize: '12px', marginTop: '6px', color: '#d1d5db' }}>
                {Z('재료명 클릭 → 자동 추가 | 단체주문에서 "발주서에 추가" 버튼 사용 가능', '点食材名称 → 自动添加')}
              </p>
            </div>
          ) : (
            <>
              {displayGroups.map(group => {
                const groupCost = group.items.reduce((s, item) => s + amountOf(item), 0);   // V135
                const isCollapsed = collapsedVendors[group.vendor];
                return (
                  <div key={group.vendor} style={{ marginBottom: '16px' }}>
                    {/* 업체 헤더 */}
                    {vendorGroups.length > 1 && (
                      <div onClick={() => toggleVendorCollapse(group.vendor)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                 padding: '9px 14px', background: '#1e40af', borderRadius: isCollapsed ? '10px' : '10px 10px 0 0',
                                 cursor: 'pointer', userSelect: 'none' }}>
                        <span style={{ fontSize: '13px', fontWeight: '800', color: '#fff' }}>
                          🏪 {vendorLabel(group.vendor)}
                          <span style={{ fontWeight: '400', fontSize: '12px', marginLeft: '8px', opacity: 0.85 }}>
                            {group.items.length}{Z('품목', ' 项')}
                          </span>
                        </span>
                        <span style={{ color: '#bfdbfe', fontSize: '13px', fontWeight: '700' }}>
                          {money(groupCost)} {isCollapsed ? '▶' : '▼'}
                        </span>
                      </div>
                    )}

                    {/* 업체 아이템 테이블 */}
                    {!isCollapsed && (
                      <div style={{ background: '#fff', borderRadius: vendorGroups.length > 1 ? '0 0 10px 10px' : '12px',
                                    border: '1px solid #e5e7eb', borderTop: vendorGroups.length > 1 ? 'none' : undefined,
                                    overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
                        <table style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#1A56A0' }}>
                              {[Z('재료명', '食材名称'), Z('규격', '规格'), Z('단가', '单价'), Z('필요량', '需要量'), Z('발주 수량', '订货数量'), Z('금액', '金额')].map((h, i) => (
                                <th key={h} style={{
                                  padding: '10px 14px', fontSize: '12px', fontWeight: '700',
                                  textAlign: i >= 2 ? 'right' : 'left',
                                  color: '#fff',
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(group.subGroups && group.subGroups.length >= 1) && (group.subGroups || []).flatMap(sg => {
                              const subRows = [];
                              const subTotal = sg.items.reduce((s, it) => s + amountOf(it), 0);   // V135
                              subRows.push(
                                <tr key={`sub-${sg.key}`}>
                                  <td colSpan={6} style={{
                                    padding: '8px 14px', background: sg.meta.bg, color: sg.meta.color,
                                    fontSize: '12px', fontWeight: '700',
                                    borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb',
                                  }}>
                                    <span style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span>{t(sg.meta.label)} ({sg.items.length}{Z('품목', ' 项')})</span>
                                      <span style={{ fontSize: '11px', opacity: 0.85 }}>{subTotal > 0 ? `${money(subTotal)}` : ''}</span>
                                    </span>
                                  </td>
                                </tr>
                              );
                              sg.items.forEach((item, fi) => {
                                const qty = qtyOf(item);
                                subRows.push(
                                  <tr key={item._idx + '-sub'} ref={el => { rowRefs.current[keyOf(item)] = el; }}
                                    style={{ borderBottom: '1px solid #f3f4f6', background: fi % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '11px 14px', fontWeight: '600', fontSize: '14px' }}><BiName ko={item.name} zhMap={zhMap} />{safeLinkHref(item.link) && <a href={safeLinkHref(item.link)} target="_blank" rel="noopener noreferrer" title={t('클릭해서 구매 페이지 열기')} style={{ display: 'inline-block', marginTop: 3, fontSize: 11, fontWeight: 700, color: '#1A56A0', textDecoration: 'none' }}>🔗 {linkLabel(item.link)}</a>}</td>
                                    <td onClick={() => toggleSpecExclude(item)}
                                      title={isSpecExcluded(item) ? Z('클릭하여 규격 발주서에 포함', '点击后订货单里显示规格') : Z('클릭하여 규격 발주서에서 제외', '点击后订货单里不显示规格')}
                                      style={{
                                        padding: '11px 14px', fontSize: '13px',
                                        color: isSpecExcluded(item) ? '#d1d5db' : '#6b7280',
                                        textDecoration: isSpecExcluded(item) ? 'line-through' : 'none',
                                        cursor: 'pointer', userSelect: 'none',
                                      }}>
                                      {biPrint && specZh(item.name, item.spec) ? <BiName ko={item.spec || '-'} zh={specZh(item.name, item.spec)} pron={false} /> : (item.spec || '-')}
                                      {isSpecExcluded(item) && <span style={{ marginLeft: 4, fontSize: 10, color: '#9ca3af' }}>({Z('제외', '不显示')})</span>}
                                    </td>
                                    <td style={{ padding: '11px 14px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                                      {priceLabel(item)}
                                    </td>
                                    <td style={{ padding: '11px 14px', textAlign: 'right', fontSize: '12px' }}>
                                      {orderNeed[keyOf(item)] > 0
                                        ? <span style={{ color: '#6b7280' }}>{needText(item)}</span>
                                        : <span style={{ color: '#d1d5db' }}>—</span>}
                                    </td>
                                    <td style={{ padding: '7px 14px', textAlign: 'right' }}>
                                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <QtyInput value={qty} onChange={v => setQty(keyOf(item), v)} min={0} size="sm" />
                                      </div>
                                      {packOf(item).pack > 1 && qty > 0 && (
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#1A56A0', marginTop: 3 }}>= {fmtQty(docQtyOf(item))}{unitShow(item)}</div>
                                      )}
                                    </td>
                                    <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: '700', color: '#FF8F00', fontSize: '14px' }}>
                                      {money(amountOf(item))}
                                    </td>
                                  </tr>
                                );
                              });
                              return subRows;
                            })}
                            {(!group.subGroups || group.subGroups.length < 1) && group.items.map((item, fi) => {
                              const qty = qtyOf(item);
                              return (
                                <tr key={item._idx} ref={el => { rowRefs.current[keyOf(item)] = el; }}
                                  style={{ borderBottom: '1px solid #f3f4f6',
                                           background: fi % 2 === 0 ? '#fff' : '#fafafa',
                                           transition: 'background 0.4s' }}>
                                  <td style={{ padding: '11px 14px', fontWeight: '600', fontSize: '14px' }}><BiName ko={item.name} zhMap={zhMap} />{safeLinkHref(item.link) && <a href={safeLinkHref(item.link)} target="_blank" rel="noopener noreferrer" title={t('클릭해서 구매 페이지 열기')} style={{ display: 'inline-block', marginTop: 3, fontSize: 11, fontWeight: 700, color: '#1A56A0', textDecoration: 'none' }}>🔗 {linkLabel(item.link)}</a>}</td>
                                  <td onClick={() => toggleSpecExclude(item)}
                                    title={isSpecExcluded(item) ? Z('클릭하여 규격 발주서에 포함', '点击后订货单里显示规格') : Z('클릭하여 규격 발주서에서 제외', '点击后订货单里不显示规格')}
                                    style={{
                                      padding: '11px 14px', fontSize: '13px',
                                      color: isSpecExcluded(item) ? '#d1d5db' : '#6b7280',
                                      textDecoration: isSpecExcluded(item) ? 'line-through' : 'none',
                                      cursor: 'pointer', userSelect: 'none',
                                    }}>
                                    {biPrint && specZh(item.name, item.spec) ? <BiName ko={item.spec || '-'} zh={specZh(item.name, item.spec)} pron={false} /> : (item.spec || '-')}
                                    {isSpecExcluded(item) && <span style={{ marginLeft: 4, fontSize: 10, color: '#9ca3af' }}>({Z('제외', '不显示')})</span>}
                                  </td>
                                  <td style={{ padding: '11px 14px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                                    {priceLabel(item)}
                                  </td>
                                  <td style={{ padding: '11px 14px', textAlign: 'right', fontSize: '12px' }}>
                                    {orderNeed[keyOf(item)] > 0
                                      ? <span style={{ color: '#6b7280' }}>{needText(item)}</span>
                                      : <span style={{ color: '#d1d5db' }}>—</span>}
                                  </td>
                                  <td style={{ padding: '7px 14px', textAlign: 'right' }}>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                      <QtyInput value={qty} onChange={v => setQty(keyOf(item), v)} min={0} size="sm" />
                                    </div>
                                    {packOf(item).pack > 1 && qty > 0 && (
                                      <div style={{ fontSize: 11, fontWeight: 700, color: '#1A56A0', marginTop: 3 }}>= {fmtQty(docQtyOf(item))}{unitShow(item)}</div>
                                    )}
                                  </td>
                                  <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: '700', color: '#FF8F00', fontSize: '14px' }}>
                                    {money(amountOf(item))}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: vendorGroups.length > 1 ? '#eff6ff' : '#FAFAFA' }}>
                              <td colSpan={5} style={{ padding: '11px 14px', fontWeight: '800',
                                                        color: vendorGroups.length > 1 ? '#1e40af' : '#FF6F0F', fontSize: '13px' }}>
                                {vendorGroups.length > 1 ? `${vName(group.vendor)} ${Z('소계', '小计')}` : Z('합계', '合计')} ({group.items.length}{Z('품목', ' 项')})
                              </td>
                              <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: '800',
                                            color: vendorGroups.length > 1 ? '#1e40af' : '#FF6F0F', fontSize: '15px' }}>
                                {money(groupCost)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* 전체 합계 (업체 2개 이상일 때) */}
              {vendorGroups.length > 1 && vendorView === 'all' && (
                <div style={{ background: '#FAFAFA', borderRadius: '10px', border: '2px solid #FF6F0F',
                              padding: '13px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '800', color: '#18181B', fontSize: '14px' }}>
                    {Z(`전체 합계 (${orderedItems.length}품목 · ${vendorGroups.length}개 업체)`, `总计（${orderedItems.length} 项 · ${vendorGroups.length} 个供应商）`)}
                  </span>
                  <span style={{ fontWeight: '800', color: '#18181B', fontSize: '17px' }}>
                    {money(totalCost)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div> {/* 패널 래퍼 닫기 */}

      {/* ── 이메일/문자 미리보기 모달 ── */}
      {preview && (
        <div onClick={e => { if (e.target === e.currentTarget) setPreview(null); }}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:3000,
                   display:'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent:'center' }}>
          <div style={{
            background:'#fff',
            borderRadius: isMobile ? '20px 20px 0 0' : '16px',
            width: isMobile ? '100%' : '600px',
            maxWidth: isMobile ? '100%' : '95vw',
            maxHeight: isMobile ? '92vh' : '88vh',
            display:'flex', flexDirection:'column',
            boxShadow:'0 20px 60px rgba(0,0,0,0.3)',
          }}>

            {/* 헤더 */}
            <div style={{ padding:'18px 24px', borderBottom:'1px solid #e5e7eb',
                          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <h3 style={{ fontSize:'16px', fontWeight:'800', color:'#18181B' }}>
                  {preview.mode === 'email' ? Z('✉️ 이메일 전송', '✉️ 发邮件') : Z('💬 문자 전송', '💬 发短信')}
                </h3>
                <p style={{ fontSize:'12px', color:'#6b7280', marginTop:'3px' }}>
                  {preview.contact.name}
                  {preview.mode === 'email' ? ` · ${preview.contact.email}` : ` · ${preview.contact.phone}`}
                </p>
              </div>
              <button onClick={() => setPreview(null)}
                style={{ background:'none', border:'none', fontSize:'20px', cursor:'pointer', color:'#9ca3af' }}>✕</button>
            </div>

            {/* 안내 */}
            <div style={{ padding:'12px 24px', background:'#FAFAFA', borderBottom:'1px solid #e5e7eb',
                          fontSize:'12px', color:'#374151', display:'flex', alignItems:'center', gap:'8px' }}>
              <span style={{ fontSize:'16px' }}>💡</span>
              {preview.mode === 'email'
                ? Z('아래 표를 드래그해서 직접 복사하거나, "표 복사" 버튼으로 클립보드에 복사 후 이메일 본문에 붙여넣기 하세요.', '可以直接选中下面的表格复制，或点「复制表格」后粘贴到邮件正文。')
                : Z('아래 텍스트를 드래그해서 복사하거나, "복사" 버튼으로 클립보드에 복사하세요.', '可以直接选中下面的文字复制，或点「复制」按钮。')}
            </div>

            {/* 미리보기 내용 */}
            <div style={{ flex:1, overflowY:'auto', padding: isMobile ? '16px' : '20px 24px' }}>
              {preview.mode === 'email' ? (
                /* HTML 표 (선택/복사 가능) — 모바일에서 좌우 스크롤 */
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <div
                    dangerouslySetInnerHTML={{ __html: buildOrderHtml() }}
                    style={{ userSelect:'text', cursor:'text', minWidth: isMobile ? '480px' : 'auto' }}
                  />
                </div>
              ) : (
                /* 텍스트 (선택/복사 가능) */
                <pre style={{
                  fontFamily:"'Malgun Gothic', sans-serif", fontSize:'13px',
                  lineHeight:1.8, color:'#374151', whiteSpace:'pre-wrap',
                  background:'#f8fafb', borderRadius:'10px', padding:'16px 18px',
                  border:'1px solid #e5e7eb', userSelect:'text', cursor:'text',
                }}>
                  {buildOrderText()}
                </pre>
              )}
            </div>

            {/* 버튼 */}
            <div style={{ padding: isMobile ? '14px 16px' : '16px 24px', borderTop:'1px solid #e5e7eb',
                          display:'flex', gap:'10px', justifyContent: isMobile ? 'stretch' : 'flex-end',
                          flexDirection: isMobile ? 'column' : 'row' }}>
              <button onClick={() => handleCopyToClipboard(preview.mode)}
                style={{ ...btnBase,
                         background: copied ? '#6b7280' : '#FAFAFA',
                         color: copied ? '#fff' : '#FF6F0F',
                         border: `1.5px solid ${copied ? '#6b7280' : '#FF6F0F'}`,
                         padding: isMobile ? '14px' : '10px 20px',
                         fontSize: isMobile ? '14px' : '13px' }}>
                {copied ? Z('✅ 복사됨!', '✅ 已复制！') : preview.mode === 'email' ? Z('📋 표 복사 (이메일에 붙여넣기)', '📋 复制表格（粘贴到邮件）') : Z('📋 텍스트 복사', '📋 复制文字')}
              </button>
              <button onClick={handleOpenApp}
                style={{ ...btnBase,
                         background: preview.mode === 'email' ? '#3b82f6' : '#FF6F0F',
                         color:'#fff',
                         padding: isMobile ? '14px' : '10px 20px',
                         fontSize: isMobile ? '14px' : '13px' }}>
                {preview.mode === 'email' ? Z('✉️ 이메일 앱 열기', '✉️ 打开邮件 App') : Z('💬 문자 앱 열기', '💬 打开短信 App')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 저장 확인 모달 ── */}
      {showSaveConfirm && (
        <div onClick={e => { if (e.target === e.currentTarget) setShowSaveConfirm(false); }}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:4000,
                   display:'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent:'center' }}>
          <div style={{ background:'#fff',
                        borderRadius: isMobile ? '20px 20px 0 0' : '16px',
                        width: isMobile ? '100%' : '400px', maxWidth:'95vw',
                        boxShadow:'0 20px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            <div style={{ padding:'20px 24px', borderBottom:'1px solid #e5e7eb' }}>
              <h3 style={{ fontSize:'16px', fontWeight:'800', color:'#18181B' }}>💾 {t('발주서 저장')}</h3>
              <p style={{ fontSize:'13px', color:'#6b7280', marginTop:'6px' }}>
                {getToday()} · {orderedItems.length}{Z('품목', ' 项')} · {money(totalCost)}
              </p>
              {/* V138: 발주처별로 따로 저장됨 */}
              <div data-save-split style={{ marginTop:'8px', display:'flex', flexDirection:'column', gap:'3px' }}>
                {groupItemsByVendor(orderedItems).map(g => (
                  <div key={g.vendor} style={{ fontSize:'12px', color:'#1e3a8a', display:'flex', justifyContent:'space-between', gap:8 }}>
                    <span>📄 {vendorLabel(g.vendor === NO_VENDOR ? '' : g.vendor) || t('발주처 미지정')} {t('발주서')}</span>
                    <span style={{ color:'#6b7280' }}>{g.items.length}{t('품목')} · {money(g.items.reduce((s, it) => s + amountOf(it), 0))}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding:'16px 24px' }}>
              {/* V137 */}
              <div style={{ fontSize:'12px', color:'#9A3412', background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:'8px',
                            padding:'8px 10px', marginBottom:'12px', lineHeight:1.55 }}>
                {t('발주처별로 발주서가 따로 저장되고, 발주서는 비워져요. 추가 발주는 새 발주서로 따로 저장돼요.')}
                <div style={{ marginTop:2, color:'#7C2D12' }}>{t('아직 안 보냈다면 먼저 📤 전송·🖨 인쇄를 하세요. 저장한 발주서도 📅 내역에서 다시 보낼 수 있어요.')}</div>
              </div>
              <label style={{ fontSize:'13px', fontWeight:'600', color:'#374151' }}>{Z('메모 (선택)', '备注（可选）')}</label>
              <input
                autoFocus
                value={saveMemo}
                onChange={e => setSaveMemo(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmSave(); if (e.key === 'Escape') setShowSaveConfirm(false); }}
                placeholder={Z('예: 월요일 발주, 추가 주문 등', '例：周一订货、追加订货')}
                style={{ width:'100%', marginTop:'8px', padding:'10px 12px', boxSizing:'border-box',
                         border:'1.5px solid #e5e7eb', borderRadius:'8px', fontSize:'13px', outline:'none' }} />
            </div>
            <div style={{ padding:'12px 24px 20px', display:'flex', gap:'10px' }}>
              <button onClick={confirmSave}
                style={{ flex:1, padding:'13px', borderRadius:'10px', border:'none',
                         background:'#FF6F0F', color:'#fff', fontSize:'14px', fontWeight:'800', cursor:'pointer' }}>
                {t('저장')}
              </button>
              <button onClick={() => setShowSaveConfirm(false)}
                style={{ padding:'13px 20px', borderRadius:'10px', border:'1px solid #e5e7eb',
                         background:'#fff', color:'#6b7280', fontSize:'14px', cursor:'pointer' }}>
                {t('취소')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 발주 내역 모달 ── */}
      {showHistoryModal && (() => {
        const allListVendors = [...new Set(
          orderHistory.flatMap(e => e.items.map(i => i.vendor || '미분류'))
        )].sort((a,b) => a==='미분류'?1:b==='미분류'?-1:a.localeCompare(b,'ko'));
        const filteredHistory = orderHistory.filter(entry => {
          if (historyDateFrom && entry.date < historyDateFrom) return false;
          if (historyDateTo   && entry.date > historyDateTo)   return false;
          if (historyListVendor !== 'all' &&
              !entry.items.some(i => (i.vendor||'미분류') === historyListVendor)) return false;
          return true;
        });
        return (
        <div onClick={e => { if (e.target === e.currentTarget) { setShowHistoryModal(false); setHistoryDetail(null); setHVendorFilter('all'); setHistoryDateFrom(''); setHistoryDateTo(''); setHistoryListVendor('all'); setMergeSel([]); setEntryCopied(''); setHEdit(null); } }}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:4000,
                   display:'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent:'center' }}>
          <div style={{ background:'#fff',
                        borderRadius: isMobile ? '20px 20px 0 0' : '16px',
                        width: isMobile ? '100%' : '620px', maxWidth:'95vw',
                        maxHeight: isMobile ? '92vh' : '85vh',
                        display:'flex', flexDirection:'column',
                        boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>

            {/* 헤더 */}
            <div style={{ padding:'18px 20px', borderBottom:'1px solid #e5e7eb',
                          display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                {historyDetail && (
                  <button onClick={() => { setHistoryDetail(null); setHVendorFilter('all'); setEntryCopied(''); setHEdit(null); }}
                    style={{ background:'none', border:'none', color:'#18181B', fontSize:'18px',
                             cursor:'pointer', padding:'0 4px', fontWeight:'700' }}>‹</button>
                )}
                <h3 style={{ fontSize:'16px', fontWeight:'800', color:'#18181B' }}>
                  {historyDetail ? `📋 ${historyDetail.date}${entryVendor(historyDetail) && entryVendor(historyDetail) !== NO_VENDOR ? ' ' + vendorLabel(entryVendor(historyDetail)) : ''} ${Z('발주서', '订货单')}` : `📅 ${Z('발주 내역', '订货记录')} (${filteredHistory.length}${filteredHistory.length !== orderHistory.length ? `/${orderHistory.length}` : ''}${Z('건', ' 张')})`}
                </h3>
              </div>
              <button onClick={() => { setShowHistoryModal(false); setHistoryDetail(null); setHVendorFilter('all'); setHistoryDateFrom(''); setHistoryDateTo(''); setHistoryListVendor('all'); setMergeSel([]); setEntryCopied(''); setHEdit(null); }}
                style={{ background:'none', border:'none', fontSize:'20px', cursor:'pointer', color:'#9ca3af' }}>✕</button>
            </div>

            {/* 필터 바 (목록 보기일 때만) */}
            {!historyDetail && orderHistory.length > 0 && (
              <div style={{ padding:'12px 16px', borderBottom:'1px solid #e5e7eb',
                            background:'#f8fafb', flexShrink:0 }}>
                {/* 날짜 범위 */}
                <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap', marginBottom:'8px' }}>
                  <span style={{ fontSize:'12px', fontWeight:'700', color:'#6b7280', minWidth:'32px' }}>📅 {Z('기간', '日期')}</span>
                  <input type="date" value={historyDateFrom} onChange={e => setHistoryDateFrom(e.target.value)}
                    style={{ padding:'5px 8px', border:'1.5px solid #e5e7eb', borderRadius:'7px',
                             fontSize:'12px', outline:'none', flex:1, minWidth:'120px' }} />
                  <span style={{ fontSize:'12px', color:'#9ca3af' }}>~</span>
                  <input type="date" value={historyDateTo} onChange={e => setHistoryDateTo(e.target.value)}
                    style={{ padding:'5px 8px', border:'1.5px solid #e5e7eb', borderRadius:'7px',
                             fontSize:'12px', outline:'none', flex:1, minWidth:'120px' }} />
                  {(historyDateFrom || historyDateTo) && (
                    <button onClick={() => { setHistoryDateFrom(''); setHistoryDateTo(''); }}
                      style={{ padding:'4px 8px', borderRadius:'6px', border:'1px solid #e5e7eb',
                               background:'#fff', fontSize:'11px', color:'#9ca3af', cursor:'pointer' }}>{t('초기화')}</button>
                  )}
                </div>
                {/* 업체 필터 */}
                {allListVendors.length > 1 && (
                  <div style={{ display:'flex', gap:'5px', flexWrap:'wrap', alignItems:'center' }}>
                    <span style={{ fontSize:'12px', fontWeight:'700', color:'#6b7280', minWidth:'32px' }}>🏪 {Z('업체', '供应商')}</span>
                    <button onClick={() => setHistoryListVendor('all')}
                      style={{ padding:'4px 10px', borderRadius:'6px', fontSize:'11px', fontWeight:'700', border:'none', cursor:'pointer',
                               background: historyListVendor==='all' ? '#FF6F0F' : '#f3f4f6',
                               color: historyListVendor==='all' ? '#fff' : '#374151' }}>{Z('전체', '全部')}</button>
                    {allListVendors.map(v => (
                      <button key={v} onClick={() => setHistoryListVendor(v)}
                        style={{ padding:'4px 10px', borderRadius:'6px', fontSize:'11px', fontWeight:'700', border:'none', cursor:'pointer',
                                 background: historyListVendor===v ? '#1e40af' : '#f3f4f6',
                                 color: historyListVendor===v ? '#fff' : '#374151' }}>{v}</button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 목록 or 상세 */}
            <div style={{ flex:1, overflowY:'auto' }}>
              {!historyDetail && onMergeOrderHistory && orderHistory.length > 1 && mergeSel.length === 0 && (
                <div style={{ padding:'10px 16px 0', fontSize:'12px', color:'#6b7280' }}>
                  🔗 {t('같은 날 따로 저장한 발주서는 ☑ 골라서 하나로 합칠 수 있어요')}
                </div>
              )}
              {!historyDetail ? (
                /* ── 목록 ── */
                orderHistory.length === 0 ? (
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
                                justifyContent:'center', height:'200px', color:'#9ca3af' }}>
                    <div style={{ fontSize:'36px', marginBottom:'10px' }}>📋</div>
                    <p style={{ fontSize:'14px', fontWeight:'600' }}>{Z('저장된 발주 내역이 없어요', '还没有保存的订货单')}</p>
                    <p style={{ fontSize:'12px', marginTop:'6px' }}>{Z('💾 저장 버튼으로 발주서를 기록하세요', '点「💾 保存」就会记录订货单')}</p>
                  </div>
                ) : filteredHistory.length === 0 ? (
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'180px', color:'#9ca3af' }}>
                    <div style={{ fontSize:'32px', marginBottom:'10px' }}>🔍</div>
                    <p style={{ fontSize:'14px', fontWeight:'600' }}>{Z('조건에 맞는 내역이 없어요', '没有符合条件的记录')}</p>
                    <button onClick={() => { setHistoryDateFrom(''); setHistoryDateTo(''); setHistoryListVendor('all'); }}
                      style={{ marginTop:'10px', padding:'7px 16px', borderRadius:'8px', border:'1.5px solid #FF6F0F',
                               background:'#fff', color:'#18181B', fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>{t('필터 초기화')}</button>
                  </div>
                ) : (
                  <div style={{ padding:'12px' }}>
                    {filteredHistory.map(entry => {
                      const picked = mergeSel.includes(entry.id);   // V137 합치기 선택
                      return (
                      <div key={entry.id} data-history-entry={entry.id}
                        style={{ border: picked ? '1.5px solid #1A56A0' : '1px solid #e5e7eb', borderRadius:'10px', padding:'14px 16px',
                                 marginBottom:'8px', background: picked ? '#EFF6FF' : '#fff', cursor:'pointer',
                                 transition:'border-color 0.15s' }}
                        onClick={() => { setHistoryDetail(entry); setHVendorFilter('all'); setEntryCopied(''); setHEdit(null); }}
                        onMouseEnter={e => { if (!picked) e.currentTarget.style.borderColor='#FF6F0F'; }}
                        onMouseLeave={e => { if (!picked) e.currentTarget.style.borderColor='#e5e7eb'; }}>
                        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                          {onMergeOrderHistory && (
                            <label onClick={e => e.stopPropagation()} title={t('합칠 발주서로 고르기')}
                              style={{ display:'flex', alignItems:'center', padding:'2px 10px 0 0', cursor:'pointer', flexShrink:0 }}>
                              <input type="checkbox" checked={picked} data-merge-pick={entry.id}
                                onChange={() => setMergeSel(s => (s.includes(entry.id) ? s.filter(x => x !== entry.id) : [...s, entry.id]))}
                                style={{ width:17, height:17, accentColor:'#1A56A0', cursor:'pointer' }} />
                            </label>
                          )}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:'700', fontSize:'14px', color:'#1a1a1a' }}>
                              {entry.date}
                              {savedTimeOf(entry) && <span style={{ marginLeft:'6px', fontSize:'12px', fontWeight:600, color:'#9ca3af' }}>{savedTimeOf(entry)}</span>}
                              {entryVendor(entry)
                                ? <span style={{ marginLeft:'8px', fontSize:'13px', fontWeight:800, color:'#1e40af' }}>🏪 {entryVendor(entry) === NO_VENDOR ? t('발주처 미지정') : vendorLabel(entryVendor(entry))} {t('발주서')}</span>
                                : entry.bizName && <span style={{ marginLeft:'8px', fontSize:'12px', color:'#18181B' }}>{entry.bizName} {Z('발주서', '订货单')}</span>}
                              {entry.editedAt && <span style={{ marginLeft:'6px', fontSize:'11px', fontWeight:700, color:'#9A3412', background:'#FFF7ED', padding:'1px 6px', borderRadius:4 }}>✏️ {t('고침')}</span>}
                              {entry.receive && (   /* V158 입고 상태 */
                                <span data-receive-badge={entry.receive.state} style={{ marginLeft:'6px', fontSize:'11px', fontWeight:700, padding:'1px 6px', borderRadius:4,
                                  color: entry.receive.state === 'full' ? '#15803D' : entry.receive.state === 'partial' ? '#9A3412' : '#6b7280',
                                  background: entry.receive.state === 'full' ? '#F0FDF4' : entry.receive.state === 'partial' ? '#FFEDD5' : '#F4F4F5' }}>
                                  {entry.receive.state === 'full' ? `📥 ${Z('입고 완료', '已入库')}` : entry.receive.state === 'partial' ? `📥 ${Z('일부 입고', '部分入库')}` : Z('입고 안 함', '不入库')}
                                </span>
                              )}
                              {entry.mergedFrom && entry.mergedFrom.length > 1 && (
                                <span style={{ marginLeft:'6px', fontSize:'11px', fontWeight:700, color:'#1A56A0', background:'#EFF6FF', padding:'1px 6px', borderRadius:4 }}>🔗 {entry.mergedFrom.length}{t('개 합침')}</span>
                              )}
                            </div>
                            {entry.memo && (
                              <div style={{ fontSize:'12px', color:'#6b7280', marginTop:'3px' }}>{entry.memo}</div>
                            )}
                            <div style={{ fontSize:'12px', color:'#9ca3af', marginTop:'4px', display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center' }}>
                              <span>{entry.items.length}{Z('품목', ' 项')} · {money(entry.totalCost||0)}</span>
                              {[...new Set(entry.items.map(i=>i.vendor||'미분류'))].filter(v=>v!=='미분류').slice(0,3).map(v=>(
                                <span key={v} style={{ background:'#eff6ff',color:'#1e40af',padding:'1px 6px',borderRadius:'4px',fontSize:'11px',fontWeight:'600' }}>{v}</span>
                              ))}
                            </div>
                          </div>
                          <div style={{ display:'flex', gap:'6px', flexShrink:0, marginLeft:'10px' }}>
                            <button onClick={e => { e.stopPropagation(); setHistoryDetail(entry); setHVendorFilter('all'); setEntryCopied(''); setHEdit(null); }}
                              style={{ ...btnBase, background:'#FAFAFA', color:'#18181B',
                                       border:'1px solid #F4F4F5', padding:'5px 10px', fontSize:'11px' }}>
                              {Z('조회', '查看')}
                            </button>
                            <button onClick={e => {
                                e.stopPropagation();
                                if (window.confirm(Z(`${entry.date} 발주서를 삭제하시겠습니까?`, `要删除 ${entry.date} 的订货单吗？`)))
                                  onDeleteOrderHistory(entry.id);
                              }}
                              style={{ ...btnBase, background:'#fff5f5', color:'#52525B',
                                       border:'1px solid #E4E4E7', padding:'5px 10px', fontSize:'11px' }}>
                              {t('삭제')}
                            </button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )
              ) : (() => {
                // 상세 보기 내 업체 그룹 (state는 컴포넌트 최상위에서 관리)
                const hVendors = [...new Set(historyDetail.items.map(i => i.vendor || '미분류'))].sort((a,b) => a==='미분류'?1:b==='미분류'?-1:a.localeCompare(b,'ko'));
                const hDisplayItems = hVendorFilter === 'all'
                  ? historyDetail.items
                  : historyDetail.items.filter(i => (i.vendor||'미분류') === hVendorFilter);

                return (
                /* ── 상세 ── */
                <div style={{ padding:'16px 20px' }}>
                  {/* 요약 카드 */}
                  <div style={{ background:'#FAFAFA', borderRadius:'10px', padding:'12px 16px',
                                marginBottom:'12px', display:'flex', gap:'16px', flexWrap:'wrap' }}>
                    <div><div style={{ fontSize:'11px', color:'#9ca3af' }}>{t('날짜')}</div>
                      <div style={{ fontWeight:'700', fontSize:'14px' }}>{historyDetail.date}</div></div>
                    <div><div style={{ fontSize:'11px', color:'#9ca3af' }}>{t('품목 수')}</div>
                      <div style={{ fontWeight:'700', fontSize:'14px' }}>{historyDetail.items.length}{Z('품목', ' 项')}</div></div>
                    <div><div style={{ fontSize:'11px', color:'#9ca3af' }}>{t('합계 금액')}</div>
                      <div style={{ fontWeight:'700', fontSize:'14px', color:'#18181B' }}>
                        {money(historyDetail.totalCost||0)}</div></div>
                    {historyDetail.memo && (
                      <div><div style={{ fontSize:'11px', color:'#9ca3af' }}>{t('메모')}</div>
                        <div style={{ fontWeight:'600', fontSize:'13px' }}>{historyDetail.memo}</div></div>
                    )}
                  </div>

                  {/* V138: 수량 고치기 */}
                  {onUpdateOrderHistory && (
                    <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', marginBottom:'10px' }}>
                      {!hEdit ? (
                        <button data-edit-start onClick={() => setHEdit({})}
                          style={{ ...btnBase, background:'#fff', color:'#1e40af', border:'1.5px solid #93C5FD', padding:'6px 12px', fontSize:'12px' }}>
                          ✏️ {t('수량 고치기')}
                        </button>
                      ) : (
                        <>
                          <span style={{ alignSelf:'center', fontSize:'12px', color:'#6b7280', marginRight:'auto' }}>{t('수량을 바꾸고 저장하세요. 0이면 그 품목을 빼요.')}</span>
                          <button data-edit-save onClick={saveHistoryEdit}
                            style={{ ...btnBase, background:'#1A56A0', color:'#fff', border:'none', padding:'6px 14px', fontSize:'12px', fontWeight:800 }}>
                            💾 {t('고친 내용 저장')}
                          </button>
                          <button onClick={() => setHEdit(null)}
                            style={{ ...btnBase, background:'#fff', color:'#6b7280', border:'1px solid #e5e7eb', padding:'6px 12px', fontSize:'12px' }}>
                            {t('취소')}
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {/* 업체 필터 */}
                  {hVendors.length > 1 && (
                    <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'12px' }}>
                      <button onClick={() => { setHVendorFilter('all'); setEntryCopied(''); }}
                        style={{ ...btnBase, fontSize:'11px', padding:'4px 10px',
                                 background: hVendorFilter==='all' ? '#FF6F0F' : '#f3f4f6',
                                 color: hVendorFilter==='all' ? '#fff' : '#6b7280', border:'none' }}>
                        {Z('전체', '全部')}
                      </button>
                      {hVendors.map(v => (
                        <button key={v} data-hvendor={v} onClick={() => { setHVendorFilter(v); setEntryCopied(''); }}
                          style={{ ...btnBase, fontSize:'11px', padding:'4px 10px',
                                   background: hVendorFilter===v ? '#1e40af' : '#f3f4f6',
                                   color: hVendorFilter===v ? '#fff' : '#374151', border:'none' }}>
                          {v}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* 품목 테이블 (업체별 그룹) */}
                  {(hVendorFilter === 'all' && hVendors.length > 1
                    ? hVendors
                    : [hVendorFilter === 'all' ? null : hVendorFilter]
                  ).map(v => {
                    const groupItems = v ? hDisplayItems.filter(i => (i.vendor||'미분류')===v) : hDisplayItems;
                    if (groupItems.length === 0) return null;
                    const groupCost = groupItems.reduce((s,i) => s + editQtyOf(i) * i.price, 0);
                    return (
                      <div key={v||'all'} style={{ marginBottom:'12px' }}>
                        {hVendors.length > 1 && v && (
                          <div style={{ padding:'7px 12px', background:'#1e40af', borderRadius:'8px 8px 0 0',
                                        fontSize:'12px', fontWeight:'800', color:'#fff' }}>
                            🏪 {vName(v)}
                          </div>
                        )}
                        <div style={{ background:'#fff', borderRadius: hVendors.length>1&&v ? '0 0 8px 8px' : '10px',
                                      border:'1px solid #e5e7eb', borderTop: hVendors.length>1&&v ? 'none':undefined, overflow:'hidden' }}>
                          <table style={{ width:'100%', borderCollapse:'collapse' }}>
                            <thead>
                              <tr style={{ background: '#1A56A0' }}>
                                <th style={{ padding:'8px 12px', fontSize:'11px', fontWeight:'700', textAlign:'left', color: '#fff' }}>{Z('재료명', '食材名称')}</th>
                                <th style={{ padding:'8px 12px', fontSize:'11px', fontWeight:'700', textAlign:'left', color: '#fff' }}>{Z('규격', '规格')}</th>
                                <th style={{ padding:'8px 8px', fontSize:'11px', fontWeight:'700', textAlign:'right', color: '#fff' }}>{Z('발주 수량', '订货数量')}</th>
                                <th style={{ padding:'8px 12px', fontSize:'11px', fontWeight:'700', textAlign:'right', color: '#fff' }}>{Z('금액', '金额')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupItems.map((item, i) => {
                                const idx = historyDetail.items.indexOf(item);
                                const eq = editQtyOf(item);
                                return (
                                <tr key={i} style={{ borderBottom:'1px solid #f3f4f6',
                                                     background: hEdit && eq === 0 ? '#FEF2F2' : (i%2===0 ? '#fff' : '#fafafa'),
                                                     opacity: hEdit && eq === 0 ? 0.6 : 1 }}>
                                  <td style={{ padding:'9px 12px', fontWeight:'600', fontSize:'13px' }}><BiName ko={item.name} zhMap={zhMap} /></td>
                                  <td style={{ padding:'9px 12px', fontSize:'12px', color:'#6b7280' }}>{specText(item.name, item.spec)}</td>
                                  <td style={{ padding:'9px 8px', textAlign:'right', fontWeight:'700',
                                               color:'#1e40af', fontSize:'13px' }}>
                                    {hEdit ? (
                                      <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:4, flexWrap:'wrap' }}>
                                        <input type="number" min="0" step="any" data-edit-qty={idx}
                                          value={Object.prototype.hasOwnProperty.call(hEdit, idx) ? hEdit[idx] : (item.pack > 1 ? (item.packs ?? item.qty / item.pack) : item.qty)}
                                          onChange={e => { const v = e.target.value; setHEdit(m => ({ ...m, [idx]: v })); }}
                                          style={{ width:64, padding:'4px 6px', border:'1.5px solid #93C5FD', borderRadius:6, textAlign:'right', fontSize:13, fontWeight:700, color:'#1e40af' }} />
                                        <span style={{ fontSize:11, color:'#6b7280', whiteSpace:'nowrap' }}>
                                          {item.pack > 1 ? `× ${fmtQty(item.pack)}${unitText(item.name, item.unit)} = ${fmtQty(eq)}${unitText(item.name, item.unit)}` : unitText(item.name, item.unit)}
                                        </span>
                                        <button title={t('이 품목 빼기')} data-edit-remove={idx} onClick={() => setHEdit(m => ({ ...m, [idx]: '0' }))}
                                          style={{ border:'none', background:'none', cursor:'pointer', fontSize:13, padding:'0 2px' }}>🗑</button>
                                      </div>
                                    ) : <>{fmtQty(item.qty)} {unitText(item.name, item.unit)}</>}
                                  </td>
                                  <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:'700',
                                               color:'#FF8F00', fontSize:'13px' }}>
                                    {money(eq * item.price)}
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr style={{ background: hVendors.length>1 ? '#eff6ff' : '#FAFAFA' }}>
                                <td colSpan={3} style={{ padding:'9px 12px', fontWeight:'800',
                                                          color: hVendors.length>1 ? '#1e40af' : '#FF6F0F', fontSize:'12px' }}>
                                  {v && hVendors.length>1 ? `${vName(v)} ${Z('소계', '小计')}` : Z('합계', '合计')} ({groupItems.length}{Z('품목', ' 项')})
                                </td>
                                <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:'800',
                                              color: hVendors.length>1 ? '#1e40af' : '#FF6F0F', fontSize:'14px' }}>
                                  {money(groupCost)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    );
                  })}

                  {/* 전체 합계 */}
                  {hVendors.length > 1 && hVendorFilter === 'all' && (
                    <div style={{ background:'#FAFAFA', borderRadius:'8px', border:'2px solid #FF6F0F',
                                  padding:'11px 16px', display:'flex', justifyContent:'space-between' }}>
                      <span style={{ fontWeight:'800', color:'#18181B', fontSize:'13px' }}>
                        {Z(`전체 합계 (${historyDetail.items.length}품목)`, `总计（${historyDetail.items.length} 项）`)}
                      </span>
                      <span style={{ fontWeight:'800', color:'#18181B', fontSize:'15px' }}>
                        {money(historyDetail.totalCost||0)}
                      </span>
                    </div>
                  )}

                  {/* 내보내기 버튼 바 — V139: 위에서 발주처를 고르면 그 발주처 것만 복사·엑셀·PDF */}
                  {hVendors.length > 1 && hVendorFilter !== 'all' && (
                    <div data-scope-note style={{ marginTop:'14px', fontSize:'12px', fontWeight:700, color:'#1e40af' }}>
                      🏪 {vendorLabel(hVendorFilter === NO_VENDOR ? '' : hVendorFilter) || t('발주처 미지정')} {t('것만 복사·엑셀·PDF로 나가요')}
                    </div>
                  )}
                  <div style={{ display:'flex', gap:'8px', marginTop: hVendors.length > 1 && hVendorFilter !== 'all' ? '6px' : '16px', flexWrap:'wrap' }}>
                    {/* V137: 저장하면 발주서가 비워지므로, 저장한 발주서를 여기서 글로 다시 보낼 수 있게 */}
                    <button onClick={() => copyEntryText(scopedDetail(historyDetail))}
                      style={{ flex:1, minWidth:'120px', padding: isMobile ? '13px 10px' : '11px 14px',
                               borderRadius:'10px', border:'none', background: cnDoc ? '#07C160' : '#1e40af', color:'#fff',
                               fontSize: isMobile ? '13px' : '12px', fontWeight:'700', cursor:'pointer',
                               display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
                      <span>{cnDoc ? '💬' : '📋'}</span> {entryCopied === 'copied' ? t('복사됐어요') : entryCopied === 'shared' ? t('보냈어요') : (cnDoc ? t('위챗 글 복사') : t('글 복사'))}
                    </button>
                    <button onClick={() => exportHistoryExcel(scopedDetail(historyDetail))}
                      style={{ flex:1, minWidth:'120px', padding: isMobile ? '13px 10px' : '11px 14px',
                               borderRadius:'10px', border:'none', background:'#FF6F0F', color:'#fff',
                               fontSize: isMobile ? '13px' : '12px', fontWeight:'700', cursor:'pointer',
                               display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
                      <span>📊</span> {Z('엑셀 다운로드', '下载 Excel')}
                    </button>
                    <button onClick={() => exportHistoryPdf(scopedDetail(historyDetail))}
                      style={{ flex:1, minWidth:'120px', padding: isMobile ? '13px 10px' : '11px 14px',
                               borderRadius:'10px', border:'none', background:'#52525B', color:'#fff',
                               fontSize: isMobile ? '13px' : '12px', fontWeight:'700', cursor:'pointer',
                               display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
                      <span>📄</span> {Z('PDF·인쇄', 'PDF · 打印')}
                    </button>
                  </div>
                </div>
                );
              })()}
            </div>
            {!historyDetail && mergeSel.length > 0 && (() => {
              const picked = orderHistory.filter(h => mergeSel.includes(h.id));
              const chk = canMergeOrders(picked);
              const sum = picked.reduce((s, h) => s + (Number(h.totalCost) || 0), 0);
              return (
                <div data-merge-bar style={{ padding:'12px 16px', borderTop:'1px solid #e5e7eb', background:'#F8FAFC', flexShrink:0,
                              display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:180, fontSize:'13px', color:'#1e3a8a', fontWeight:700 }}>
                    {picked.length}{t('개 선택')} · {money(sum)}
                    {chk.reason === 'month' && <div style={{ fontSize:'11px', color:'#B91C1C', fontWeight:600, marginTop:2 }}>{t('다른 달 발주서는 합칠 수 없어요 (월 정산 금액이 바뀌어요)')}</div>}
                    {chk.reason === 'few' && <div style={{ fontSize:'11px', color:'#6b7280', fontWeight:600, marginTop:2 }}>{t('2개 이상 고르세요')}</div>}
                  </div>
                  <button onClick={() => doMerge(picked)} disabled={!chk.ok}
                    style={{ padding:'10px 16px', borderRadius:'9px', border:'none', background: chk.ok ? '#1A56A0' : '#CBD5E1',
                             color:'#fff', fontSize:'13px', fontWeight:800, cursor: chk.ok ? 'pointer' : 'default' }}>
                    🔗 {t('합치기')}
                  </button>
                  <button onClick={() => setMergeSel([])}
                    style={{ padding:'10px 12px', borderRadius:'9px', border:'1px solid #e5e7eb', background:'#fff', color:'#6b7280', fontSize:'12px', cursor:'pointer' }}>
                    {t('선택 해제')}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
        );
      })()}

      {/* ── 저장·전송 모달 ── */}
      {showShareModal && (
        <div onClick={e => { if (e.target === e.currentTarget) closeShareModal(); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000,
                   display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
          <div style={{
            background: '#fff',
            borderRadius: isMobile ? '20px 20px 0 0' : '16px',
            width: isMobile ? '100%' : '500px',
            maxWidth: isMobile ? '100%' : '95vw',
            maxHeight: isMobile ? '92vh' : '85vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>

            {/* 모달 헤더 */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#18181B' }}>📤 {Z('발주서 저장·전송', '保存 · 发送订货单')}</h3>
              <button onClick={closeShareModal}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9ca3af' }}>✕</button>
            </div>

            {/* 탭 */}
            <div style={{ display: 'flex', padding: '14px 24px 0', gap: '4px', borderBottom: '1px solid #e5e7eb' }}>
              {[['save', Z('💾 저장', '💾 保存')], ['send', cnDoc ? Z('💬 위챗 · 전송', '💬 微信 · 发送') : Z('📤 전송', '📤 发送')]].map(([id, label]) => (
                <button key={id} onClick={() => setShareTab(id)}
                  style={{ padding: '9px 20px', borderRadius: '8px 8px 0 0', fontSize: '13px', fontWeight: '700',
                           cursor: 'pointer', border: 'none', marginBottom: '-1px',
                           background: shareTab === id ? '#FF6F0F' : '#f3f4f6',
                           color: shareTab === id ? '#fff' : '#6b7280' }}>
                  {label}
                </button>
              ))}
            </div>

            {/* 탭 내용 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px' : '20px 24px' }}>

              {/* ── 저장 탭 */}
              {shareTab === 'save' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                    {Z('파일로 저장하거나 인쇄하세요', '保存成文件或打印')}
                  </p>
                  <button onClick={handleExcelDownload}
                    style={{ background: '#FF6F0F', color: '#fff', padding: isMobile ? '16px' : '14px 20px',
                             borderRadius: '10px', border: 'none', cursor: 'pointer',
                             display: 'flex', alignItems: 'center', gap: '12px', textAlign: 'left' }}>
                    <span style={{ fontSize: '28px' }}>📊</span>
                    <div>
                      <div style={{ fontSize: isMobile ? '15px' : '14px', fontWeight: '700' }}>{Z('엑셀 다운로드 (.xlsx)', '下载 Excel (.xlsx)')}</div>
                      <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '2px' }}>{Z('Microsoft Excel / Google Sheets에서 열 수 있어요', '可以用 Excel、WPS 打开')}</div>
                    </div>
                  </button>
                  <button onClick={handlePdfDownload}
                    style={{ background: '#52525B', color: '#fff', padding: isMobile ? '16px' : '14px 20px',
                             borderRadius: '10px', border: 'none', cursor: 'pointer',
                             display: 'flex', alignItems: 'center', gap: '12px', textAlign: 'left' }}>
                    <span style={{ fontSize: '28px' }}>📄</span>
                    <div>
                      <div style={{ fontSize: isMobile ? '15px' : '14px', fontWeight: '700' }}>{t('PDF 저장')}</div>
                      <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '2px' }}>
                        {isMobile ? Z('새 창 열림 → 인쇄 → PDF 저장', '打开新页面 → 打印 → 保存为 PDF') : Z('새 창이 열리면 인쇄 → PDF로 저장 선택', '打开新页面后，打印时选「保存为 PDF」')}
                      </div>
                    </div>
                  </button>
                  {/* 모바일 전용: 클립보드 복사 바로가기 */}
                  {isMobile && (
                    <button onClick={async () => {
                        try {
                          await navigator.clipboard.write([
                            new ClipboardItem({
                              'text/html': new Blob([buildOrderHtml()], { type: 'text/html' }),
                              'text/plain': new Blob([buildOrderText()], { type: 'text/plain' }),
                            })
                          ]);
                          alert(lang === 'zh' ? '已复制，可以粘贴到微信等' : cnDoc ? '표가 복사됐어요! 위챗·메모 등에 붙여넣기 하세요.' : '표가 클립보드에 복사됐어요!\n이메일·메모·카카오 등에 붙여넣기 하세요.');
                        } catch {
                          await navigator.clipboard.writeText(buildOrderText());
                          alert(Z('텍스트가 복사됐어요!\n붙여넣기 하세요.', '文字已复制，请粘贴。'));
                        }
                      }}
                      style={{ background: '#1e40af', color: '#fff', padding: '16px',
                               borderRadius: '10px', border: 'none', cursor: 'pointer',
                               display: 'flex', alignItems: 'center', gap: '12px', textAlign: 'left' }}>
                      <span style={{ fontSize: '28px' }}>📋</span>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: '700' }}>{Z('표 복사 (붙여넣기용)', '复制表格（用来粘贴）')}</div>
                        <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '2px' }}>{lang === 'zh' ? '可直接粘贴到微信、备忘录等' : cnDoc ? '위챗·메모 등에 바로 붙여넣기' : '이메일·카카오·메모 등에 바로 붙여넣기'}</div>
                      </div>
                    </button>
                  )}
                </div>
              )}

              {/* ── 전송 탭 */}
              {shareTab === 'send' && (
                <div>
                  {/* V133: 중국 매장 — 위챗 발주 (카카오 대신) */}
                  {cnDoc && (
                    <div style={{ marginBottom: '16px' }}>
                      <button onClick={handleWechatSend}
                        style={{ ...btnBase, background: '#07C160', color: '#fff', width: '100%', padding: '14px', fontSize: '15px', fontWeight: 800 }}>
                        💬 {Z('위챗으로 보내기', '发微信')}
                      </button>
                      <div style={{ fontSize: '12px', color: wechatState === 'fail' ? '#B91C1C' : '#52525B', marginTop: '6px', lineHeight: 1.5 }}>
                        {wechatState === 'copied' ? Z('✅ 복사됐어요 — 위챗 대화창에 붙여넣으세요', '✅ 已复制，请粘贴到微信对话框')
                          : wechatState === 'shared' ? Z('✅ 공유창으로 보냈어요', '✅ 已发送')
                          : wechatState === 'fail' ? Z('⚠ 복사가 안 됐어요 — 아래 글을 길게 눌러 복사하세요', '⚠ 复制失败，请长按下面的文字复制')
                          : Z('중국어 발주서 글을 복사해요 (휴대폰은 공유창). 금액은 들어가지 않아요', '复制中文订货单（手机会打开分享），不含金额，粘贴到微信即可')}
                      </div>
                      <pre style={{ marginTop: '8px', padding: '12px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px',
                                    fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '220px', overflowY: 'auto',
                                    fontFamily: 'inherit', userSelect: 'text', WebkitUserSelect: 'text' }}>{buildOrderText()}</pre>
                    </div>
                  )}
                  {/* 연락처 헤더 */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#374151' }}>
                      {Z('연락처', '联系人')} <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '400' }}>({contacts.length}{Z('명', '位')})</span>
                    </span>
                    <button onClick={() => { setEditingContact(null); setCf({ name:'', email:'', phone:'' }); setShowContactForm(true); }}
                      style={{ ...btnBase, background: '#FAFAFA', color: '#18181B', border: '1.5px solid #FF6F0F', padding: '7px 14px' }}>
                      {Z('+ 연락처 추가', '+ 添加联系人')}
                    </button>
                  </div>

                  {/* 연락처 폼 */}
                  {showContactForm && (
                    <div style={{ background: '#f8fafb', border: '1.5px solid #FF6F0F', borderRadius: '10px',
                                  padding: '16px', marginBottom: '14px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#18181B', marginBottom: '10px' }}>
                        {editingContact ? Z('✏️ 연락처 편집', '✏️ 编辑联系人') : Z('+ 새 연락처', '+ 新联系人')}
                      </div>
                      {[
                        { key: 'name', placeholder: Z('이름 *', '姓名 *'), type: 'text' },
                        { key: 'phone', placeholder: Z('전화번호 (예: 010-1234-5678)', '电话（例：138-0000-0000）'), type: 'tel' },
                        { key: 'email', placeholder: Z('이메일 주소', '邮箱地址'), type: 'email' },
                      ].map(({ key, placeholder, type }) => (
                        <input key={key} type={type} placeholder={placeholder}
                          value={cf[key]}
                          onChange={e => setCf(prev => ({ ...prev, [key]: e.target.value }))}
                          style={{ width: '100%', marginBottom: '8px', padding: '9px 12px', boxSizing: 'border-box',
                                   border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
                      ))}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                        <button onClick={handleSaveContact}
                          style={{ ...btnBase, background: '#FF6F0F', color: '#fff', flex: 1, padding: '10px' }}>{t('저장')}</button>
                        <button onClick={() => { setShowContactForm(false); setEditingContact(null); }}
                          style={{ ...btnBase, background: '#f3f4f6', color: '#6b7280', padding: '10px 16px' }}>{t('취소')}</button>
                      </div>
                    </div>
                  )}

                  {/* 연락처 없을 때 */}
                  {contacts.length === 0 && !showContactForm && (
                    <div style={{ textAlign: 'center', padding: '28px 0', color: '#9ca3af' }}>
                      <div style={{ fontSize: '36px', marginBottom: '10px' }}>👤</div>
                      <p style={{ fontSize: '14px', fontWeight: '600' }}>{Z('저장된 연락처가 없어요', '还没有联系人')}</p>
                      <p style={{ fontSize: '12px', marginTop: '6px' }}>{Z('연락처를 추가하면 빠르게 전송할 수 있어요', '添加联系人后可以快速发送')}</p>
                    </div>
                  )}

                  {/* 연락처 목록 */}
                  {contacts.map(contact => (
                    <div key={contact.id}
                      style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '13px 14px', marginBottom: '8px', background: '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '14px', color: '#1a1a1a' }}>{contact.name}</div>
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '3px', lineHeight: 1.6 }}>
                            {contact.phone && <span>📱 {contact.phone}</span>}
                            {contact.phone && contact.email && <br />}
                            {contact.email && <span>✉️ {contact.email}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '5px', flexShrink: 0, marginLeft: '8px' }}>
                          <button onClick={() => startEditContact(contact)}
                            style={{ ...btnBase, background: '#f3f4f6', color: '#6b7280', padding: '5px 10px', fontSize: '11px' }}>{t('편집')}</button>
                          <button onClick={() => handleDeleteContact(contact.id)}
                            style={{ ...btnBase, background: '#fff5f5', color: '#52525B', padding: '5px 10px', fontSize: '11px', border: '1px solid #E4E4E7' }}>{t('삭제')}</button>
                        </div>
                      </div>
                      {/* 전송 버튼 */}
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {contact.phone && (
                          <button onClick={() => handleSmsSend(contact)}
                            style={{ ...btnBase, background: '#FF6F0F', color: '#fff', flex: 1, padding: '9px 6px', fontSize: '12px' }}>
                            💬 {Z('문자', '短信')}
                          </button>
                        )}
                        {contact.email && (
                          <button onClick={() => handleEmailSend(contact)}
                            style={{ ...btnBase, background: '#3b82f6', color: '#fff', flex: 1, padding: '9px 6px', fontSize: '12px' }}>
                            ✉️ {Z('이메일', '邮件')}
                          </button>
                        )}
                        {!cnDoc && (
                        <button onClick={handleKakaoSend}
                          style={{ ...btnBase, background: '#fee500', color: '#191919', flex: 1, padding: '9px 6px', fontSize: '12px' }}>
                          💛 카카오
                        </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* 연락처 없어도 카카오 공유 가능 */}
                  {contacts.length === 0 && !cnDoc && (
                    <button onClick={handleKakaoSend}
                      style={{ ...btnBase, background: '#fee500', color: '#191919', width: '100%', padding: '12px', fontSize: '13px', marginTop: '8px' }}>
                      💛 카카오톡으로 바로 공유
                    </button>
                  )}

                  {/* 카카오 앱키 안내 */}
                  {!cnDoc && (
                  <div style={{ marginTop: '16px', padding: '10px 14px', background: '#FAFAFA', borderRadius: '8px',
                                border: '1px solid #D4D4D8', fontSize: '11px', color: '#52525B' }}>
                    💡 카카오 전송은 <strong>.env</strong> 파일에 <code>REACT_APP_KAKAO_KEY=앱키</code> 설정 필요
                  </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <BusinessInfoModal
        open={showBizModal}
        initialInfo={{ ...bizInfo, name: bizInfo.name || bizName }}
        onSave={handleSaveBizInfo}
        onClose={() => setShowBizModal(false)}
      />
    </div>
  );
}

export default PurchaseOrder;
